// ─── PART 5: OPTIONS TAB ──────────────────────────────────────────────────────
// Dependencies: part1_core (C, s, fmt, pct, fmtK, bps, DataStatus, AnalystButton)
// Exports: OptionsTab
//
// Data sources (in priority order):
//   Tradier API   — real options chain with Black-Scholes Greeks (primary)
//   Yahoo Finance — unofficial chain endpoint, approximated Greeks (fallback)
//   FRED API      — VIX (VIXCLS) and SKEW index, 252-day history for IV rank
//   Alpaca REST   — underlying spot price + change
//
// onData callbacks pushed to globalSignals:
//   "vix"          — { current, history[], min52, max52 }
//   "skew"         — number
//   "pcRatio"      — { volumeRatio, oiRatio, callVol, putVol, callOI, putOI }
//   "unusualOptions" — [{ strike, volume, oi, ratio, isCall, ticker }]
//
// GEX computation:
//   Calls: dealer short → +GEX (long gamma for market)
//   Puts:  dealer long  → -GEX (short gamma for market)
//   Net GEX per strike normalized for bar chart display
//   Dealer Magnet = strike with highest abs(GEX) × OI score
//
// Greeks display:
//   OFF by default — chain shows Vol, OI, IV only
//   ON  — adds Delta, Gamma, Theta, Vega columns
//   Gamma column gets subtle teal heat background scaled to magnitude
//   Asterisk (*) on approximated Greeks (Yahoo fallback)

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { AlertCircle } from "lucide-react";
import {
  C, s, font, fmt, pct, fmtK, bps,
  DataStatus, AnalystButton, backendFetch,
  FRED_BASE, ALPACA_REST, TRADIER_BASE,
} from "./core";
import { HestonSurfacePanel } from "./CryptoSentiment";

// ─── GREEK APPROXIMATIONS (Yahoo fallback when Tradier unavailable) ───────────
// Moneyness-based — directionally correct, not numerically precise.
// Flagged with * in the UI so the user always knows the source.
const approxDelta = (strike, spot, isCall) => {
  if (!spot) return "—";
  const m = (spot - strike) / spot;
  return isCall
    ? Math.max(0.01, Math.min(0.99, 0.5 + m * 5)).toFixed(3)
    : Math.max(-0.99, Math.min(-0.01, -0.5 + m * 5)).toFixed(3);
};

const approxGamma = (strike, spot) => {
  if (!spot) return "—";
  return Math.max(0, 0.08 - (Math.abs(spot - strike) / spot) * 0.4).toFixed(5);
};

// ─── GEX COMPUTATION ──────────────────────────────────────────────────────────
function computeGex(calls, puts, spot) {
  const strikes = {};

  calls.forEach((c) => {
    if (!strikes[c.strike]) strikes[c.strike] = { strike: c.strike, gex: 0, oi: 0 };
    const g = parseFloat(c.gamma) || 0;
    // Dealers typically short calls → long gamma exposure per call OI
    strikes[c.strike].gex += g * (c.oi || 0) * 100 * (spot || 1);
    strikes[c.strike].oi  += c.oi || 0;
  });

  puts.forEach((p) => {
    if (!strikes[p.strike]) strikes[p.strike] = { strike: p.strike, gex: 0, oi: 0 };
    const g = parseFloat(p.gamma) || 0;
    // Dealers typically long puts → short gamma exposure per put OI
    strikes[p.strike].gex -= g * (p.oi || 0) * 100 * (spot || 1);
    strikes[p.strike].oi  += p.oi || 0;
  });

  const liquid = Object.values(strikes)
    .filter((s) => s.oi > 10)
    .sort((a, b) => a.strike - b.strike);

  // Keep liquid strikes within ±20% of spot — far-OTM strikes carry negligible
  // GEX and (via normalization) shrink the meaningful bars. BUT never filter
  // everything out: if spot is missing/zero/stale (e.g. the quote hasn't loaded
  // yet on a ticker change) the ±20% window can exclude every strike, which is
  // what made GEX intermittently disappear. Fall back to all liquid strikes.
  let arr = liquid;
  if (spot > 0) {
    const near = liquid.filter((s) => Math.abs(s.strike - spot) <= spot * 0.20);
    if (near.length) arr = near;
  }

  const maxAbs = Math.max(...arr.map((s) => Math.abs(s.gex)), 1);

  return arr.map((s) => ({
    ...s,
    gexN:  s.gex / maxAbs,                      // normalized -1 to +1 for bar chart
    gexM:  (s.gex / 1e6).toFixed(2),            // millions USD label
  }));
}

// ─── NET DEALER DELTA (DEX) ───────────────────────────────────────────────────
// Aggregate dealer delta exposure in dollar terms. Standard DEX convention:
// dealers are assumed short the open interest customers are long, so dealer delta
// = −(net OI delta), with call deltas positive and put deltas negative. The result
// can flip sign: NEGATIVE = dealers net short delta (mechanically buy into strength,
// sell weakness); POSITIVE = net long. `delta` is a per-row string on the chain.
function computeNetDealerDelta(calls, puts, spot) {
  let netOi = 0;
  (calls || []).forEach((c) => { netOi += (parseFloat(c.delta) || 0) * (c.oi || 0); });
  (puts  || []).forEach((p) => { netOi += (parseFloat(p.delta) || 0) * (p.oi || 0); });
  return -netOi * 100 * (spot || 1);
}

// Compact next-earnings line for the AI analyst context. Returns "" when there is
// no upcoming event so callers can append it unconditionally — never emits a
// "no earnings" note. Trailing newline keeps it safe in front of any following text.
function earningsContextLine(e) {
  if (!e || !e.date) return "";
  const away = typeof e.days_until === "number" ? `${e.days_until}d away` : "upcoming";
  const mkt = e.time && e.time !== "unspecified" ? `, ${e.time} market` : "";
  let out = `\nNext earnings: ${e.date}\n(${away}${mkt})`;
  if (e.eps_estimate) out += `\nEPS estimate: ${e.eps_estimate}`;
  return out + "\n";
}

// ─── DEALER MAGNET ────────────────────────────────────────────────────────────
function findMagnet(gexData) {
  if (!gexData.length) return { strike: null, type: null };
  const scored = [...gexData].sort(
    (a, b) => Math.abs(b.gex) * b.oi - Math.abs(a.gex) * a.oi
  );
  const top = scored[0];
  return {
    strike: top.strike,
    type:   top.gex > 0 ? "PIN" : "ACCEL",
  };
}

// ─── UNUSUAL VOLUME SCREEN ────────────────────────────────────────────────────
function computeUnusual(calls, puts, ticker) {
  return [...calls, ...puts]
    .filter((o) => o.oi > 100 && o.volume > 0 && o.volume / o.oi > 3)
    .map((o) => ({
      strike:  o.strike,
      volume:  o.volume,
      oi:      o.oi,
      ratio:   o.volume / o.oi,
      isCall:  !o.itm,          // approximate — ITM puts / OTM calls
      iv:      o.iv,
      bid:     o.bid,
      ask:     o.ask,
      ticker,
    }))
    .sort((a, b) => b.ratio - a.ratio)
    .slice(0, 10);
}

// ─── 0DTE INTELLIGENCE HELPERS ────────────────────────────────────────────────
// "0DTE" = the nearest listed expiry when it falls on today or the next trading
// day (weekends roll to Monday). Metrics compute client-side from that chain.

// ET wall-clock parts for an instant (no external tz lib).
function _etParts(date = new Date()) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York", hour12: false, weekday: "short",
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });
  const p = Object.fromEntries(dtf.formatToParts(date).map((x) => [x.type, x.value]));
  return { ymd: `${p.year}-${p.month}-${p.day}`, weekday: p.weekday };
}

// Minutes America/New_York is offset from UTC at a given instant (handles DST).
function _etOffsetMinutes(instantMs) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York", hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const p = Object.fromEntries(dtf.formatToParts(instantMs).map((x) => [x.type, x.value]));
  const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
  return (asUTC - instantMs) / 60000;
}

// Absolute ms of 4:00 PM ET (market close) on a YYYY-MM-DD date.
function _expiryCloseMs(ymd) {
  const [y, m, d] = ymd.split("-").map(Number);
  const naive = Date.UTC(y, m - 1, d, 16, 0, 0);   // 4PM treated as UTC
  return naive - _etOffsetMinutes(naive) * 60000;  // shift to the real 4PM-ET instant
}

// Next calendar day (or next trading day when tradingOnly), as YYYY-MM-DD.
function _nextDay(ymd, tradingOnly) {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  do { dt.setUTCDate(dt.getUTCDate() + 1); }
  while (tradingOnly && (dt.getUTCDay() === 0 || dt.getUTCDay() === 6));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
}

// Is `expiryYmd` a 0DTE expiry (today or the next trading day, ET)?
function classify0DTE(expiryYmd) {
  const now = _etParts();
  const isWeekend = now.weekday === "Sat" || now.weekday === "Sun";
  const tomorrow = _nextDay(now.ymd, isWeekend);   // weekend → roll to next trading day
  const is0dte = expiryYmd === now.ymd || expiryYmd === tomorrow;
  const hoursToExpiry = (_expiryCloseMs(expiryYmd) - Date.now()) / 3600000;
  return { is0dte, hoursToExpiry };
}

// Max pain = settlement strike minimizing total in-the-money option value (the
// price at which option writers pay out the least). Candidates = listed strikes.
function computeMaxPain(calls, puts) {
  const strikes = [...new Set([...calls, ...puts].map((o) => o.strike))]
    .filter((k) => typeof k === "number").sort((a, b) => a - b);
  if (strikes.length < 2) return null;
  let best = null, bestLoss = Infinity;
  for (const S of strikes) {
    let loss = 0;
    for (const c of calls) loss += (c.oi || 0) * Math.max(0, S - c.strike);
    for (const p of puts)  loss += (p.oi || 0) * Math.max(0, p.strike - S);
    if (loss < bestLoss) { bestLoss = loss; best = S; }
  }
  return best;
}

// ─── VIX GAUGE COMPONENT ──────────────────────────────────────────────────────
function VixGauge({ vix, ivRank, status }) {
  if (!vix) return (
    <div style={{ ...s.panelB, color: C.textDim, fontSize: 13 }}>
      VIX data unavailable
    </div>
  );

  const color = vix.current > 30 ? C.red : vix.current > 20 ? C.amber : vix.current < 12 ? C.orange : C.green;
  const label = vix.current > 30 ? "HIGH FEAR" : vix.current > 20 ? "ELEVATED" : vix.current < 12 ? "COMPLACENT" : "NORMAL";

  return (
    <div style={s.panelB}>
      <div style={s.lbl}>VIX — IMPLIED VOL INDEX</div>
      <div style={{ fontSize: 30, fontWeight: 700, color, lineHeight: 1 }}>
        {fmt(vix.current)}
      </div>
      <div style={{ fontSize: 11, color, marginTop: 2, marginBottom: 6, fontWeight: 700 }}>
        {label}
      </div>

      {ivRank != null && (
        <div style={{ marginBottom: 8 }}>
          <div style={s.lbl}>IV RANK — 52-WEEK PERCENTILE</div>
          <div style={{
            fontSize: 13,
            fontWeight: 700,
            color: ivRank > 70 ? C.red : ivRank > 40 ? C.amber : C.green,
          }}>
            {fmt(ivRank, 0)}th
          </div>
          <div style={{ fontSize: 11, color: C.textDim }}>
            52W: {fmt(vix.min52)}–{fmt(vix.max52)}
          </div>
        </div>
      )}

      {vix.history?.length > 0 && (
        <ResponsiveContainer width="100%" height={50}>
          <LineChart data={vix.history}>
            <Line
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={1.5}
              dot={false}
            />
            <ReferenceLine y={20} stroke={C.border} strokeDasharray="3 3" />
            <ReferenceLine y={30} stroke={C.red + "44"} strokeDasharray="3 3" />
            <Tooltip
              contentStyle={{ background: C.panel, border: `1px solid ${C.border}`, fontSize: 11, fontFamily: font }}
              formatter={(v) => [fmt(v), "VIX"]}
              labelFormatter={(l) => l}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ─── GEX PROFILE CHART ────────────────────────────────────────────────────────
// GEX notional formatter ($X.XXB / $X.XXM / $XK).
const fmtGexN = (v) => {
  const a = Math.abs(Number(v) || 0), sign = v < 0 ? "-" : "";
  if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${sign}$${Math.round(a / 1e3)}K`;
  return `${sign}$${Math.round(a)}`;
};

// Custom terminal-styled GEX tooltip (dark bg, platinum text, gamma read).
function GexTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  const gexN = d.gexN ?? 0;
  const nearZero = Math.abs(gexN) < 0.05;
  const label = nearZero
    ? "NEUTRAL GAMMA"
    : gexN >= 0 ? "LONG GAMMA — stabilizing" : "SHORT GAMMA — acceleration zone";
  const labelColor = nearZero ? C.textDim : gexN >= 0 ? C.green : C.red;
  return (
    <div style={{
      background: "rgba(11, 14, 20, 0.95)",
      border: "1px solid rgba(255, 255, 255, 0.12)",
      color: C.text,
      fontFamily: font,
      fontSize: 11,
      padding: "8px 12px",
      borderRadius: 0,
      lineHeight: 1.5,
    }}>
      <div>Strike: ${d.strike}</div>
      <div>GEX: {fmtGexN(d.gex)}</div>
      <div style={{ color: labelColor, marginTop: 2 }}>{label}</div>
    </div>
  );
}

// ⚠⚠ ONE MAPPING for the Greeks label, shared by the chart and the AI context.
// The context used to say "Tradier Black-Scholes" whenever Greeks were real,
// including when the chain came from Alpaca, and named a provider the model
// could repeat back on screen. Same words as the chart, no provider names.
export function greeksLabel(hasRealGreeks, greeksSource) {
  if (!hasRealGreeks) return "Approximated";
  return greeksSource === "alpaca" ? "Reported" : "Black-Scholes";
}

function GexChart({ gexData, spot, magnet, hasRealGreeks, greeksSource, histContext }) {
  if (!gexData.length) return null;
  const greeks = greeksLabel(hasRealGreeks, greeksSource);

  // Zone context (additive) — long/short gamma strike ranges, flip nearest spot,
  // and net-GEX regime. Read-only over gexData; the bars/tooltip are untouched.
  const pos = gexData.filter((g) => g.gex > 0);
  const neg = gexData.filter((g) => g.gex < 0);
  const longLo  = pos.length ? Math.min(...pos.map((g) => g.strike)) : null;
  const longHi  = pos.length ? Math.max(...pos.map((g) => g.strike)) : null;
  const shortLo = neg.length ? Math.min(...neg.map((g) => g.strike)) : null;
  const shortHi = neg.length ? Math.max(...neg.map((g) => g.strike)) : null;
  let flip = null; // sign-change boundary strike nearest to spot (gexData is sorted)
  for (let i = 1; i < gexData.length; i++) {
    const a = gexData[i - 1], b = gexData[i];
    if (a.gex !== 0 && b.gex !== 0 && Math.sign(a.gex) !== Math.sign(b.gex)) {
      const cand = Math.abs(b.gex) < Math.abs(a.gex) ? b.strike : a.strike;
      if (flip === null || (spot > 0 && Math.abs(cand - spot) < Math.abs(flip - spot))) flip = cand;
    }
  }
  const netGex = gexData.reduce((acc, g) => acc + g.gex, 0);
  const badge = (color) => ({ fontSize: 9, padding: "3px 8px", border: `1px solid ${color}`, color, borderRadius: 2 });

  return (
    <>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 4 }}>
        {longLo != null && <span style={badge(C.green)}>LONG γ ${longLo}–${longHi}</span>}
        {shortLo != null && <span style={badge(C.red)}>SHORT γ ${shortLo}–${shortHi}</span>}
        {flip != null && <span style={badge(C.amber)}>FLIP ${flip}</span>}
      </div>
      {netGex !== 0 && (
        <div style={{ fontSize: 9, color: netGex > 0 ? C.green : C.red, marginBottom: 4 }}>
          {netGex > 0
            ? "LONG GAMMA REGIME — vol suppression, mean reversion favored"
            : "SHORT GAMMA REGIME — vol expansion, trend acceleration favored"}
        </div>
      )}
      <PctContext ctx={histContext} pct={histContext?.gex?.percentile} />

      <div style={{ fontSize: 11, color: C.textDim, marginBottom: 4 }}>
        Spot <span style={{ color: C.amber }}>${fmt(spot)}</span>
        {" · "}
        Magnet <span style={{ color: C.teal }}>${magnet.strike || "—"}</span>
        {" · "}
        {hasRealGreeks
          ? <span style={{ color: C.green }}>{greeks} Greeks</span>
          : <span style={{ color: C.amber }}>⚠ {greeks} Greeks</span>}
      </div>

      <ResponsiveContainer width="100%" height={160}>
        <BarChart data={gexData} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
          <XAxis
            dataKey="strike"
            tick={{ fontSize: 10, fill: C.textDim }}
            tickLine={false}
            tickFormatter={(v) => `$${v}`}
            interval={Math.max(0, Math.floor(gexData.length / 8) - 1)}
          />
          <YAxis
            tick={{ fontSize: 10, fill: C.textDim }}
            tickLine={false}
            axisLine={false}
            domain={[-1, 1]}
            tickFormatter={(v) => v.toFixed(1)}
          />
          <Tooltip cursor={{ fill: "rgba(255,255,255,0.05)" }} content={<GexTooltip />} />
          <ReferenceLine y={0} stroke={C.border} />
          {spot > 0 && (
            <ReferenceLine
              x={Math.round(spot / 5) * 5}
              stroke={C.amber}
              strokeDasharray="4 2"
              label={{ value: "SPOT", position: "top", fill: C.amber, fontSize: 10 }}
            />
          )}
          {magnet.strike && (
            <ReferenceLine
              x={magnet.strike}
              stroke={C.teal}
              strokeDasharray="4 2"
              label={{ value: "MAGNET", position: "top", fill: C.teal, fontSize: 10 }}
            />
          )}
          <Bar
            dataKey="gexN"
            isAnimationActive={false}
            shape={(props) => {
              const { x, y, width, height, value } = props;
              // Near-zero = |gexN| < 0.05 (i.e. |gex| < 5% of max abs GEX).
              const nearZero = Math.abs(value) < 0.05;
              const fill = value >= 0
                ? (nearZero ? "rgba(34, 197, 94, 0.4)" : C.green)   // long gamma — stabilizing
                : (nearZero ? "rgba(255, 82, 82, 0.4)" : C.red);    // short gamma — acceleration
              // Minimum 2px so every strike stays visible regardless of magnitude.
              const h  = Math.max(2, Math.abs(height));
              const ty = value >= 0 ? y : y + height;
              return <rect x={x} y={ty} width={width} height={h} fill={fill} rx={1} />;
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    </>
  );
}

// ─── OPTIONS CHAIN TABLE ──────────────────────────────────────────────────────
function ChainTable({ data, label, color, spot, showGreeks, storageKey }) {
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem(storageKey) === "1"; } catch { return false; }
  });
  const toggle = () => {
    setExpanded((e) => {
      const next = !e;
      try { localStorage.setItem(storageKey, next ? "1" : "0"); } catch {}
      return next;
    });
  };

  if (!data.length) return null;

  // Collapsed (default): near-money strikes only — within ±10% of spot, or the
  // top 20 by open interest when spot is unavailable. Expanded: the full chain.
  const near = spot
    ? data.filter((o) => Math.abs(o.strike - spot) / spot <= 0.10)
    : [...data].sort((a, b) => (b.oi || 0) - (a.oi || 0)).slice(0, 20);
  const filtered = expanded ? data : near;

  if (!filtered.length) return null;

  const cols = [
    "STRIKE", "LAST", "BID", "ASK", "VOL", "OI", "IV%",
    ...(showGreeks ? ["Δ", "Γ", "Θ", "V"] : []),
  ];

  return (
    <div style={s.panel}>
      <div style={s.panelH}>
        <span style={{ ...s.panelT, color }}>{label}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ ...s.tag(), fontSize: 11 }}>{expanded ? "ALL" : (spot ? "±10%" : "TOP OI")} · {filtered.length} rows</span>
          <span
            onClick={toggle}
            style={{
              fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", color: C.amber,
              cursor: "pointer", padding: "4px 8px", whiteSpace: "nowrap",
              border: `1px solid ${C.amber}44`, borderRadius: 2,
            }}
          >
            {expanded ? "▲ SHOW LESS" : `▼ SHOW ALL ${data.length}`}
          </span>
        </div>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {cols.map((h) => (
                <th key={h} style={{
                  padding: "3px 6px",
                  textAlign: "right",
                  fontSize: 11,
                  fontWeight: 700,
                  color: showGreeks && h === "Γ" ? C.teal : C.textDim,
                  letterSpacing: "0.06em",
                  whiteSpace: "nowrap",
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((o, i) => {
              const atm      = spot && Math.abs(o.strike - spot) < spot * 0.006;
              const highVol  = o.oi > 100 && o.volume / o.oi > 3;
              const gammaVal = parseFloat(o.gamma) || 0;
              const gammaHeat = showGreeks
                ? Math.min(1, gammaVal / 0.02)
                : 0;

              return (
                <tr key={i} style={{
                  borderBottom: `1px solid ${C.border}`,
                  background: atm
                    ? color + "12"
                    : highVol
                    ? C.amber + "08"
                    : "transparent",
                }}>
                  {/* Strike */}
                  <td style={{
                    padding: "4px 6px",
                    textAlign: "right",
                    fontWeight: atm ? 700 : 400,
                    color: atm ? color : C.text,
                    whiteSpace: "nowrap",
                  }}>
                    ${o.strike}
                    {atm && <span style={{ fontSize: 10, color, marginLeft: 3 }}>◈ATM</span>}
                  </td>
                  {/* Last */}
                  <td style={{ padding: "4px 6px", textAlign: "right", color: C.textMid }}>
                    {o.last ? `$${fmt(o.last)}` : "—"}
                  </td>
                  {/* Bid */}
                  <td style={{ padding: "4px 6px", textAlign: "right", color: C.textDim }}>
                    {o.bid ? `$${fmt(o.bid)}` : "—"}
                  </td>
                  {/* Ask */}
                  <td style={{ padding: "4px 6px", textAlign: "right", color: C.textDim }}>
                    {o.ask ? `$${fmt(o.ask)}` : "—"}
                  </td>
                  {/* Volume */}
                  <td style={{
                    padding: "4px 6px",
                    textAlign: "right",
                    color: highVol ? C.amber : o.volume > 500 ? C.text : C.textDim,
                    fontWeight: highVol ? 700 : 400,
                  }}>
                    {fmtK(o.volume)}
                    {highVol && <span style={{ fontSize: 10, color: C.amber, marginLeft: 2 }}>⚡</span>}
                  </td>
                  {/* OI */}
                  <td style={{ padding: "4px 6px", textAlign: "right", color: C.textMid }}>
                    {fmtK(o.oi)}
                  </td>
                  {/* IV */}
                  <td style={{ padding: "4px 6px", textAlign: "right", color: C.textDim }}>
                    {o.iv !== "—" ? `${o.iv}%` : "—"}
                  </td>
                  {/* Greeks — only when toggled on */}
                  {showGreeks && (
                    <>
                      <td style={{ padding: "4px 6px", textAlign: "right", color: C.blue, fontSize: 11 }}>
                        {o.delta}
                      </td>
                      <td style={{
                        padding: "4px 6px",
                        textAlign: "right",
                        fontSize: 11,
                        color: C.teal,
                        fontWeight: gammaHeat > 0.7 ? 700 : 400,
                        background: `rgba(20,184,166,${gammaHeat * 0.18})`,
                      }}>
                        {o.gamma}
                        {!o.greeksReal && (
                          <span style={{ color: C.amberDim, fontSize: 10 }}>*</span>
                        )}
                      </td>
                      <td style={{ padding: "4px 6px", textAlign: "right", color: C.textDim, fontSize: 11 }}>
                        {o.theta || "—"}
                      </td>
                      <td style={{ padding: "4px 6px", textAlign: "right", color: C.textDim, fontSize: 11 }}>
                        {o.vega || "—"}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!expanded && (
        <div style={{ fontSize: 9, color: C.textDim, padding: "6px 8px 0" }}>
          SHOWING {filtered.length} NEAR-MONEY STRIKES {spot ? "(±10%)" : "(TOP OI)"}
        </div>
      )}
      {expanded && (
        <div
          onClick={toggle}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            minHeight: 44, cursor: "pointer",
            fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", color: C.amber,
            borderTop: `1px solid ${C.border}`, marginTop: 6,
          }}
        >
          ▲ SHOW LESS
        </div>
      )}
    </div>
  );
}

// ─── UNUSUAL VOLUME TABLE ─────────────────────────────────────────────────────
function UnusualTable({ unusual, ticker, anthropicKey, quote, ivRank, earningsData }) {
  if (!unusual.length) return null;

  // Spot + day context for the AI analyst. Options flow is uninterpretable
  // without knowing where spot sits relative to the strikes (ITM/ATM/OTM).
  const spot = quote?.price || 0;
  const num = (v, d = 2) => (v != null && !Number.isNaN(v) ? fmt(v, d) : "N/A");
  // The "cluster" the flow is centered on: the unusual strike nearest to spot.
  const nearestStrike = spot > 0
    ? unusual.reduce(
        (best, o) => (Math.abs(o.strike - spot) < Math.abs(best - spot) ? o.strike : best),
        unusual[0].strike,
      )
    : unusual[0].strike;
  const distanceToStrike = spot > 0
    ? ((nearestStrike - spot) / spot * 100).toFixed(2)
    : null;

  const underlyingContext =
    `UNDERLYING: ${ticker}\n` +
    `SPOT: $${num(spot)}\n` +
    `DAY RANGE: $${num(quote?.dayLow)} - $${num(quote?.dayHigh)}\n` +
    `PREV CLOSE: $${num(quote?.prevClose)}\n` +
    `CHANGE: ${num(quote?.changePct)}%\n` +
    `VOLUME: ${quote?.dayVolume != null ? fmtK(quote.dayVolume) : "N/A"}\n` +
    (ivRank != null ? `IV RANK (52w): ${num(ivRank, 0)}th pct\n` : "") +
    `NEAREST STRIKE CLUSTER: $${nearestStrike}\n` +
    `DISTANCE TO CLUSTER: ${distanceToStrike != null ? distanceToStrike : "N/A"}%\n` +
    `(negative = cluster is below spot = OTM puts\n` +
    ` positive = cluster is above spot = OTM calls)\n\n`;

  return (
    <div style={s.panel}>
      <div style={s.panelH}>
        <span style={s.panelT}>UNUSUAL VOLUME SCREEN</span>
        <span style={{ ...s.tag(C.amber), fontSize: 11 }}>
          Vol/OI &gt; 3x · Min 100 OI · Sorted by ratio
        </span>
      </div>
      <div style={s.panelB}>
        {/* ⚠⚠ SCROLLER INSIDE s.panelB, NOT AROUND THE PANEL. s.panel paints
            the background and border; a table wider than the panel overflows that
            painted box and renders on bare app background, which is the "text with
            no container" the mobile report described. Nesting it here keeps the
            panel at its own viewport-constrained width and moves only the table.
            Same structure as ChainTable above (:485) and WatchlistTab.
            ⚠ NO minWidth, deliberately. Eight columns of option data already
            exceed a phone panel on their own, so a floor would be inert — measured
            on the Watchlist table, whose real content floor was 642px against a
            560px floor that therefore never once applied. Let the content decide. */}
        <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 10 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {["TYPE", "STRIKE", "VOL", "OI", "VOL/OI", "IV%", "BID", "ASK"].map((h) => (
                <th key={h} style={{
                  padding: "3px 6px",
                  textAlign: "right",
                  fontSize: 11,
                  color: C.textDim,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {unusual.map((o, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                <td style={{ padding: "5px 6px", textAlign: "right" }}>
                  <span style={{
                    ...s.tag(o.isCall ? C.green : C.red),
                    fontSize: 11,
                  }}>
                    {o.isCall ? "CALL" : "PUT"}
                  </span>
                </td>
                <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 700 }}>
                  ${o.strike}
                </td>
                <td style={{ padding: "5px 6px", textAlign: "right", color: C.amber, fontWeight: 700 }}>
                  {fmtK(o.volume)}
                </td>
                <td style={{ padding: "5px 6px", textAlign: "right", color: C.textMid }}>
                  {fmtK(o.oi)}
                </td>
                <td style={{ padding: "5px 6px", textAlign: "right", color: C.red, fontWeight: 700 }}>
                  {fmt(o.ratio, 1)}x
                </td>
                <td style={{ padding: "5px 6px", textAlign: "right", color: C.textDim }}>
                  {o.iv !== "—" ? `${o.iv}%` : "—"}
                </td>
                <td style={{ padding: "5px 6px", textAlign: "right", color: C.textDim }}>
                  {o.bid ? `$${fmt(o.bid)}` : "—"}
                </td>
                <td style={{ padding: "5px 6px", textAlign: "right", color: C.textDim }}>
                  {o.ask ? `$${fmt(o.ask)}` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        {(anthropicKey || window.QUANT_SIGNAL_BACKEND) && (
          <AnalystButton
            panel="unusual_options"
            label="SYNTHESIZE"
            context={
              underlyingContext +
              earningsContextLine(earningsData) +
              `Unusual options activity in ${ticker}:\n` +
              unusual.slice(0, 5).map((o) =>
                `$${o.strike} ${o.isCall ? "CALL" : "PUT"}: ` +
                `${fmtK(o.volume)} vol / ${fmtK(o.oi)} OI = ${fmt(o.ratio, 1)}x ratio, IV ${o.iv}%`
              ).join("\n") +
              `\n\nAnalyze: what this flow pattern suggests directionally, ` +
              `how far OTM these strikes are relative to spot, ` +
              `whether IV levels suggest the buyer is paying up, ` +
              `and whether the put/call mix indicates hedging vs speculation.`
            }
          />
        )}
      </div>
    </div>
  );
}

// ─── GREEKS REFERENCE ─────────────────────────────────────────────────────────
function GreeksReference({ hasRealGreeks }) {
  const greeks = [
    {
      symbol: "Δ DELTA",
      color:  C.blue,
      desc:   "Rate of option price change per $1 move in the underlying. Calls: 0 to +1. Puts: -1 to 0. ATM ≈ ±0.50. Also approximates probability of expiring in-the-money. Dealers hedge Delta by buying/selling the underlying — this is the mechanical link between options and stock price.",
    },
    {
      symbol: "Γ GAMMA",
      color:  C.teal,
      desc:   "Rate of Delta change per $1 move. Highest at ATM and near expiration. High Gamma means dealer Delta hedges need frequent adjustment — creating self-reinforcing flows. Positive GEX (dealers long Gamma) = stabilizing. Negative GEX (dealers short Gamma) = accelerating. This is the pinning and acceleration mechanic.",
    },
    {
      symbol: "Θ THETA",
      color:  C.red,
      desc:   "Daily time decay — how much option value erodes per day. Option sellers collect Theta. Buyers pay it. Accelerates exponentially in the final 30 days before expiration. A long option position is a race between directional move (Delta/Gamma gain) and time decay (Theta loss).",
    },
    {
      symbol: "V VEGA",
      color:  C.purple,
      desc:   "Sensitivity to implied volatility changes. Long options = long Vega (benefit from IV expansion / VIX spikes). Short options = short Vega (hurt by vol spikes). Vega is highest for longer-dated options. An IV rank above 70th percentile suggests Vega is expensive — option buyers are paying up for vol that may mean-revert.",
    },
  ];

  return (
    <div style={s.panel}>
      <div style={s.panelH}>
        <span style={s.panelT}>GREEKS REFERENCE</span>
        {!hasRealGreeks && (
          <span style={{ ...s.tag(C.amber), fontSize: 11 }}>
            * = approximated
          </span>
        )}
      </div>
      <div style={{
        ...s.panelB,
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: 8,
      }}>
        {greeks.map((g) => (
          <div key={g.symbol} style={{
            background: "#040608",
            border: `1px solid ${C.border}`,
            borderRadius: 3,
            padding: 10,
          }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: g.color, marginBottom: 6 }}>
              {g.symbol}
            </div>
            <div style={{ fontSize: 11, color: C.textMid, lineHeight: 1.7 }}>
              {g.desc}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── OPTIONS TAB ──────────────────────────────────────────────────────────────
// ─── rBERGOMI (ROUGH BERGOMI) — TERM-STRUCTURE CALIBRATION ────────────────────
// Rough volatility model (fractional BM, Hurst H<0.5) — rougher paths than
// Heston's standard BM. Complements Heston: rBergomi fits short-dated (≤30 DTE),
// Heston long-dated. H is estimated by OLS regression of log(ATM IV) on log(T)
// over a multi-expiry IV term structure fetched from
// /api/options/termstructure. η and ρ are approximate proxies (η from ATM-IV
// dispersion, ρ from the roughness regime) — full smile data would sharpen them.

// H interpretation label + color (shared by the display and the AI context).
function rbergomiHLabel(H) {
  if (H < 0.10) return { text: "Very rough",          color: C.green };
  if (H < 0.15) return { text: "Rough vol confirmed", color: C.green };
  if (H < 0.20) return { text: "Moderately rough",    color: C.amber };
  return { text: "Near-standard", color: C.textDim };
}

function calibrateRBergomi(termStructure) {
  if (!termStructure || termStructure.available !== true) return null;
  const pts = (termStructure.points || []).filter((p) => p && p.iv > 0 && p.T > 0);
  if (pts.length < 3) return null;

  // Step 1 — H via OLS of log(iv) on log(T): slope = H − 0.5.
  const xs = pts.map((p) => Math.log(p.T));
  const ys = pts.map((p) => Math.log(p.iv));
  const n = xs.length;
  const mx = xs.reduce((a, b) => a + b, 0) / n;
  const my = ys.reduce((a, b) => a + b, 0) / n;
  let sxy = 0, sxx = 0;
  for (let i = 0; i < n; i++) { sxy += (xs[i] - mx) * (ys[i] - my); sxx += (xs[i] - mx) ** 2; }
  if (sxx === 0) return null;
  const slope = sxy / sxx;
  const H = Math.max(0.05, Math.min(0.49, slope + 0.5));

  // R² of the log-log regression.
  let ssRes = 0, ssTot = 0;
  for (let i = 0; i < n; i++) {
    const yPred = my + slope * (xs[i] - mx);
    ssRes += (ys[i] - yPred) ** 2;
    ssTot += (ys[i] - my) ** 2;
  }
  const r2 = ssTot > 0 ? 1 - ssRes / ssTot : 0;
  const fit = r2 > 0.80 ? "good" : r2 > 0.50 ? "moderate" : "poor";

  // Step 2 — η proxy from ATM-IV dispersion vs T range (approximate: no smile).
  const ivs = pts.map((p) => p.iv);
  const Ts = pts.map((p) => p.T);
  const volRange = Math.max(...ivs) - Math.min(...ivs);
  const tRange = Math.max(...Ts) - Math.min(...Ts);
  const eta = Math.max(0.1, Math.min(3.0, tRange > 0 ? (volRange / Math.sqrt(tRange)) * 2.0 : 0.5));

  // Step 3 — ρ proxy from the roughness regime (approximate: no smile).
  const rho = H < 0.15 ? -0.70 : H < 0.25 ? -0.60 : -0.50;

  // Step 4 — model comparison + recommendation.
  const shortDatedFit = H < 0.20 ? "rBergomi preferred — rough vol regime" : "Comparable to Heston";
  const longDatedFit = H < 0.10 ? "rBergomi may still apply — very rough" : "Heston preferred for longer expiries";
  const Hs = H.toFixed(3);
  const recommendation = H < 0.15
    ? `Rough volatility confirmed (H=${Hs}). rBergomi preferred for near-term options. Heston for expiries beyond 30 days.`
    : H < 0.20
    ? `Moderately rough volatility (H=${Hs}). rBergomi and Heston comparable. Monitor term structure for regime shift.`
    : `Near-standard volatility (H=${Hs}). Heston is the preferred model. rBergomi provides limited additional edge.`;

  return { H, eta, rho, fit, r2, shortDatedFit, longDatedFit, recommendation, termStructurePoints: n };
}

// ─── MULTI-EXPIRY TERM STRUCTURE (additive rBergomi enhancement) ──────────────
// Derive an rBergomi-compatible ATM-IV term structure from the multi-expiry
// chain (GET /api/options/chain/multi) — the SAME data source GEX uses. Per
// expiry: mid_iv (decimal) at the strike closest to spot, call side preferred
// (parity with the backend's ATM point). Output shape matches the
// /api/options/termstructure response so calibrateRBergomi + RBergomiPanel
// consume it unchanged. Returns null with < 3 usable points.
function deriveTermStructureFromMulti(multi, spot) {
  if (!multi || !Array.isArray(multi.expiries) || !(spot > 0)) return null;
  const points = [];
  for (const e of multi.expiries) {
    const opts = e?.options || [];
    const dte = e?.days_to_expiry;
    if (!opts.length || !(dte > 0)) continue;
    const calls = opts.filter((o) => o.option_type === "call");
    const side = calls.length ? calls : opts;
    let atm = null, best = Infinity;
    for (const o of side) {
      if (typeof o.strike !== "number") continue;
      const d = Math.abs(o.strike - spot);
      if (d < best) { best = d; atm = o; }
    }
    const iv = atm?.greeks?.mid_iv;
    if (!(iv > 0)) continue;
    points.push({
      expiry_date: e.expiration,
      T: +(dte / 365).toFixed(6),
      strike: atm.strike,
      iv: +Number(iv).toFixed(6),
      dte,
    });
  }
  if (points.length < 3) return null;
  points.sort((a, b) => a.T - b.T);
  return { available: true, source: "multi_chain", points, point_count: points.length };
}

// Merge the term-structure endpoint points (wide DTE spread) with the
// multi-chain-derived near-term points, deduped by DTE. Strictly additive —
// never fewer points than either source. null if < 3 combined points.
function mergeTermStructures(endpointTs, derivedTs) {
  const byDte = new Map();
  const add = (ts) => {
    if (ts && ts.available === true && Array.isArray(ts.points)) {
      for (const p of ts.points) {
        if (p && p.iv > 0 && p.T > 0 && Number.isFinite(p.dte) && !byDte.has(p.dte)) {
          byDte.set(p.dte, p);
        }
      }
    }
  };
  add(endpointTs);   // endpoint first — its wider DTE buckets win on a tie
  add(derivedTs);
  const points = [...byDte.values()].sort((a, b) => a.T - b.T);
  if (points.length < 3) return null;
  return { available: true, source: "merged", points, point_count: points.length };
}

// ─── rBERGOMI ROUGHNESS VISUALIZATION (illustrative fBm paths) ────────────────
// Illustrative only — NOT market data. Conveys the CHARACTER of roughness at a
// given Hurst H: low H = high-frequency reversals (rough), high H = slow
// trending moves (smooth). Uses an AR(1) increment whose persistence scales with
// H (negative → jagged reversals, positive → trending), then cumulates.
function _genRoughPath(H, steps) {
  // H∈(0,0.5] → increment persistence: strongly negative (rough) at low H,
  // mildly positive (trending) near H=0.5. Clamped for stability.
  const persistence = Math.max(-0.85, Math.min(0.7, -0.75 + (H - 0.10) * 3.46));
  const k = Math.sqrt(1 - persistence * persistence);
  const path = [];
  let inc = Math.random() * 2 - 1;
  let acc = 0;
  for (let i = 0; i < steps; i++) {
    const shock = Math.random() * 2 - 1;
    inc = persistence * inc + k * shock;
    acc += inc;
    path.push(acc);
  }
  return path;
}

// Path values → SVG polyline points in a 0..100 (x) × 0..80 (y) viewBox, y
// inverted with vertical padding. Paired with non-scaling-stroke so the line
// stays crisp when the SVG stretches to the panel width.
function _roughPoints(path) {
  const W = 100, Hgt = 80, pad = 8;
  const n = path.length;
  let min = Infinity, max = -Infinity;
  for (const v of path) { if (v < min) min = v; if (v > max) max = v; }
  const range = (max - min) || 1;
  return path
    .map((v, i) => {
      const x = (i / (n - 1)) * W;
      const y = Hgt - pad - ((v - min) / range) * (Hgt - 2 * pad);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

function RoughnessPanels({ H }) {
  // Regenerate when the calibrated H changes (the reference H's are constant).
  const paths = useMemo(() => ({
    rough:  _roughPoints(_genRoughPath(0.10, 90)),
    center: _roughPoints(_genRoughPath(H, 90)),
    smooth: _roughPoints(_genRoughPath(0.49, 90)),
  }), [H]);

  const centerColor = H < 0.15 ? C.red : H < 0.25 ? C.amber : C.green;
  const interp =
    H < 0.15
      ? { text: "Rough volatility confirmed — standard models underprice short-dated options in this regime.", color: C.red }
      : H < 0.25
      ? { text: "Moderately rough — near-term options may be slightly mispriced.", color: C.amber }
      : { text: "Near-standard volatility — Heston provides adequate fit.", color: C.green };

  const svg = (points, color) => (
    <svg viewBox="0 0 100 80" preserveAspectRatio="none" style={{ width: "100%", height: 80, display: "block" }}>
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        vectorEffect="non-scaling-stroke"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );

  const panel = (points, color, border, topLabel, topLabelColor, hText, descriptor) => (
    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
      <div style={{ height: 12, marginBottom: 2, textAlign: "center" }}>
        {topLabel && (
          <span style={{ fontSize: 8, fontWeight: 700, color: topLabelColor, letterSpacing: "0.1em" }}>{topLabel}</span>
        )}
      </div>
      <div style={{ border, borderRadius: 2, overflow: "hidden", background: C.bg }}>
        {svg(points, color)}
      </div>
      <div style={{ textAlign: "center", marginTop: 3 }}>
        <div style={{ fontSize: 9, color: C.textDim }}>H = {hText}</div>
        <div style={{ fontSize: 8, color: C.textDim }}>{descriptor}</div>
      </div>
    </div>
  );

  // The amber highlight moves to the panel matching the calibrated regime (same
  // 0.15 / 0.25 thresholds as centerColor). The reference panel it lands on is
  // relabelled "YOUR REGIME"; the center is always "YOUR SURFACE" — amber only in
  // the moderate/center band, dimmed (C.textDim) when a reference panel matches.
  const AMBER_BORDER = `2px solid ${C.amber}`;
  const PLAIN_BORDER = `1px solid ${C.border}`;
  const rough = H < 0.15;
  const moderate = H >= 0.15 && H < 0.25;
  const standard = H >= 0.25;

  return (
    <div style={{ marginTop: 10, marginBottom: 6 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
        {panel(paths.rough,  C.red,       rough ? AMBER_BORDER : PLAIN_BORDER,    rough ? "YOUR REGIME" : null,    C.amber,                        fmt(0.10, 2), "Very rough")}
        {panel(paths.center, centerColor, moderate ? AMBER_BORDER : PLAIN_BORDER, "YOUR SURFACE",                  moderate ? C.amber : C.textDim, fmt(H, 2),    "Your surface")}
        {panel(paths.smooth, C.green,     standard ? AMBER_BORDER : PLAIN_BORDER, standard ? "YOUR REGIME" : null, C.amber,                        fmt(0.49, 2), "Near-standard")}
      </div>
      <div style={{ fontSize: 10, color: interp.color, textAlign: "center", marginTop: 8, lineHeight: 1.5 }}>
        {interp.text}
      </div>
    </div>
  );
}

// ─── rBERGOMI DISPLAY PANEL ───────────────────────────────────────────────────
function RBergomiPanel({ termStructure, result, loading, multiExpiryCount = 0 }) {
  const header = (
    <div style={{ fontSize: 11, color: C.purple, fontWeight: 700, letterSpacing: "0.15em", marginBottom: 8 }}>
      ROUGH VOLATILITY — rBERGOMI
    </div>
  );
  const wrap = (body) => <div style={{ ...s.panel, padding: "10px 12px" }}>{header}{body}</div>;

  if (loading) {
    return wrap(<div style={{ fontSize: 10, color: C.textDim }}>Fetching volatility term structure...</div>);
  }
  if (!termStructure || termStructure.available !== true) {
    const fetched = termStructure?.point_count ?? 0;
    const avail = termStructure?.expiry_count ?? termStructure?.point_count ?? 0;
    return wrap(
      <div style={{ fontSize: 10, color: C.textDim, lineHeight: 1.6 }}>
        rBergomi requires volatility term structure across 3+ expiries.
        <br />
        Fetched {fetched} expiry / {avail} expiries available.
      </div>
    );
  }
  if (!result) {
    return wrap(
      <div style={{ fontSize: 10, color: C.textDim }}>
        rBergomi requires volatility term structure across 3+ expiries.
      </div>
    );
  }
  if (result.fit === "poor") {
    return wrap(
      <div style={{ fontSize: 10, color: C.textDim }}>
        Term structure fit quality insufficient for reliable calibration (R²={fmt(result.r2, 2)}).
      </div>
    );
  }

  const pts = termStructure.points || [];
  const dteMin = Math.min(...pts.map((p) => p.dte));
  const dteMax = Math.max(...pts.map((p) => p.dte));
  const hLabel = rbergomiHLabel(result.H);
  const fitColor = result.fit === "good" ? C.green : result.fit === "moderate" ? C.amber : C.red;
  const prow = (label, value, note, noteColor) => (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 2 }}>
      <span style={{ color: C.textDim }}>{label}</span>
      <span>
        <span style={{ color: C.text, fontWeight: 700 }}>{value}</span>
        <span style={{ color: noteColor || C.textDim, fontSize: 10, marginLeft: 6 }}>{note}</span>
      </span>
    </div>
  );

  return wrap(
    <>
      <div style={{ fontSize: 9, color: C.textDim, marginBottom: 8 }}>
        Calibrated from {result.termStructurePoints}-point IV term structure · {dteMin}–{dteMax} DTE
      </div>
      {multiExpiryCount > 0 && (
        <div style={{ fontSize: 9, color: C.textDim, marginBottom: 8, marginTop: -4 }}>
          Calibrated from {multiExpiryCount} expiries (multi-expiry chain)
        </div>
      )}
      {prow("H (Hurst)", fmt(result.H, 3), hLabel.text, hLabel.color)}
      {prow("η (Vol/Vol)", fmt(result.eta, 3), "approximate")}
      {prow("ρ (Correl)", fmt(result.rho, 3), "approximate")}

      {/* Roughness visualization — illustrative fBm paths (additive) */}
      <RoughnessPanels H={result.H} />

      <div style={{ fontSize: 9, color: C.textDim, letterSpacing: "0.1em", marginTop: 10, marginBottom: 4 }}>TERM STRUCTURE</div>
      {pts.map((p) => (
        <div key={p.expiry_date} style={{ fontSize: 10, color: C.textDim, lineHeight: 1.5 }}>
          {p.dte}D&nbsp;&nbsp;${fmt(p.strike)}&nbsp;&nbsp;IV: {fmt(p.iv * 100, 1)}%
        </div>
      ))}
      <div style={{ fontSize: 10, color: fitColor, fontWeight: 700, marginTop: 4 }}>
        Fit R²: {fmt(result.r2, 2)} ({result.fit})
      </div>

      <div style={{ fontSize: 9, color: C.textDim, letterSpacing: "0.1em", marginTop: 10, marginBottom: 4 }}>MODEL COMPARISON</div>
      <div style={{ fontSize: 10, color: C.textMid, marginBottom: 2 }}>SHORT DATED (≤30 DTE): {result.shortDatedFit}</div>
      <div style={{ fontSize: 10, color: C.textMid }}>LONG DATED (&gt;30 DTE): {result.longDatedFit}</div>
      <div style={{ fontSize: 10, color: C.textDim, fontStyle: "italic", marginTop: 8, lineHeight: 1.5 }}>
        {result.recommendation}
      </div>
    </>
  );
}

// ─── 0DTE INTELLIGENCE PANEL ──────────────────────────────────────────────────
function ZeroDtePanel({ data, metrics, spot }) {
  if (!data) return null;   // not yet checked / loading — render nothing

  if (!data.available) {
    // No 0DTE expiry for this symbol — show the message only, not the panel.
    return (
      <div style={{ fontSize: 10, color: C.textDim, marginTop: 10 }}>
        No 0DTE options available for {data.symbol}
      </div>
    );
  }
  if (!metrics) return null;

  const { netGex, gammaFlip, pcVol, pcOI, maxPain, topCalls, topPuts, hoursToExpiry, regime } = metrics;
  const hrs = hoursToExpiry > 0 ? `${fmt(hoursToExpiry, 1)} hours remaining` : "expired";
  const pcColor = (v) => (v == null ? C.textDim : v > 1.5 ? C.red : v < 0.7 ? C.green : C.textDim);
  const netGexM = netGex / 1e6;
  const gexLabel = Math.abs(netGexM) >= 1000
    ? `${netGexM >= 0 ? "+" : ""}${fmt(netGexM / 1000, 2)}B`
    : `${netGexM >= 0 ? "+" : ""}${fmt(netGexM, 1)}M`;
  const maxPainPct = spot > 0 && maxPain != null ? (maxPain / spot - 1) * 100 : null;
  const accel = hoursToExpiry > 0 && hoursToExpiry <= 2;

  const tile = (label, value, color) => (
    <div style={{ flex: "1 1 110px", minWidth: 104 }}>
      <div style={{ fontSize: 9, color: C.textDim, letterSpacing: "0.08em", marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: color || C.text }}>{value}</div>
    </div>
  );
  const oiCol = (label, color, rows) => (
    <div>
      <div style={{ fontSize: 9, color, letterSpacing: "0.08em", marginBottom: 4 }}>{label}</div>
      {rows.length ? rows.map((o, i) => (
        <div key={i} style={{ fontSize: 11, color: C.text, marginBottom: 2 }}>
          ${fmt(o.strike, 0)} <span style={{ color: C.textDim }}>({fmtK(o.oi)} OI)</span>
        </div>
      )) : <div style={{ fontSize: 10, color: C.textDim }}>—</div>}
    </div>
  );

  return (
    <div style={{ ...s.panel, padding: "10px 12px", marginTop: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: C.red, fontWeight: 700, letterSpacing: "0.15em" }}>0DTE INTELLIGENCE</span>
        <span style={{ fontSize: 9, color: C.textDim }}>Expires: {data.expiry} · {hrs}</span>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 10 }}>
        {tile("0DTE NET GEX", gexLabel, netGex >= 0 ? C.green : C.red)}
        {tile("P/C VOLUME", pcVol == null ? "—" : fmt(pcVol, 2), pcColor(pcVol))}
        {tile("P/C OI", pcOI == null ? "—" : fmt(pcOI, 2), pcColor(pcOI))}
        {tile("MAX PAIN", maxPain == null ? "—"
          : `$${fmt(maxPain, 0)}${maxPainPct == null ? "" : ` (${maxPainPct >= 0 ? "+" : ""}${fmt(maxPainPct, 1)}%)`}`, C.text)}
        {tile("GAMMA FLIP", gammaFlip == null ? "—" : `$${fmt(gammaFlip, 0)}`, C.amber)}
      </div>

      <div style={{ fontSize: 10, color: regime === "LONG_GAMMA" ? C.green : C.red, marginBottom: 2 }}>
        {regime === "LONG_GAMMA"
          ? "LONG GAMMA — dealers dampen moves (vol suppressed near pins)"
          : "SHORT GAMMA — dealers amplify moves (vol accelerates on breaks)"}
      </div>
      <div style={{ fontSize: 10, color: C.textDim, marginBottom: accel ? 2 : 8 }}>
        {hoursToExpiry > 0 ? `${fmt(hoursToExpiry, 1)} hours to expiry` : "Past expiry"}
      </div>
      {accel && (
        <div style={{ fontSize: 10, color: C.red, fontWeight: 700, marginBottom: 8 }}>
          ⚠ Gamma acceleration zone: last 2 hours
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {oiCol("TOP CALLS (OI)", C.green, topCalls)}
        {oiCol("TOP PUTS (OI)", C.red, topPuts)}
      </div>
    </div>
  );
}

// ─── VOL SKEW PANEL ───────────────────────────────────────────────────────────
// C.emerald is not a palette token — the design's "Quantum Emerald" accent is
// C.amber (#00FF87); used for the header per the spec's intent.
function SkewPanel({ data, vixCur, vix3m, histContext }) {
  if (!data) return null;
  const { callSkew, putSkew, spread } = data;

  // Map a skew ratio (≈0.80–1.30 of ATM) to a 0–100% marker position.
  const pos = (ratio) => Math.max(0, Math.min(100, ((ratio - 0.80) / 0.50) * 100));
  const bar = (title, ratio, toColor) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: C.textDim, marginBottom: 3 }}>
        <span>{title}</span>
        <span style={{ color: C.text, fontWeight: 700 }}>{fmt(ratio, 2)}</span>
      </div>
      <div style={{ position: "relative", height: 8, borderRadius: 2, background: `linear-gradient(90deg, ${C.textDim}, ${toColor})` }}>
        <div style={{ position: "absolute", left: `${pos(ratio)}%`, top: -2, width: 2, height: 12, background: C.text, transform: "translateX(-1px)" }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 8, color: C.textDim, marginTop: 2, letterSpacing: "0.05em" }}>
        <span>CHEAP</span><span>EXPENSIVE</span>
      </div>
    </div>
  );

  const spreadInfo = spread > 0.05
    ? { color: C.green, lines: ["Calls expensive relative to puts", "Unusual — typically precedes upside momentum or squeeze"] }
    : spread < -0.05
    ? { color: C.textDim, lines: ["Puts expensive relative to calls", "Normal equity skew — downside protection premium"] }
    : { color: C.textDim, lines: ["Balanced skew"] };

  const inverted = vixCur != null && vix3m != null ? vixCur > vix3m : null;

  return (
    <div style={{ ...s.panel, padding: "10px 12px", marginTop: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: C.amber, fontWeight: 700, letterSpacing: "0.15em" }}>VOL SKEW</span>
        <span style={{ fontSize: 9, color: C.textDim }}>Call Skew: {fmt(callSkew, 2)} · Put Skew: {fmt(putSkew, 2)}</span>
      </div>

      {bar("25Δ CALL SKEW (call IV / ATM IV)", callSkew, C.green)}
      {bar("25Δ PUT SKEW (put IV / ATM IV)", putSkew, C.red)}

      <div style={{ marginTop: 4, marginBottom: 6 }}>
        <div style={{ fontSize: 11, color: C.text, fontWeight: 700 }}>Spread: {spread >= 0 ? "+" : ""}{fmt(spread, 3)}</div>
        {spreadInfo.lines.map((ln, i) => (
          <div key={i} style={{ fontSize: 10, color: spreadInfo.color, lineHeight: 1.5 }}>{ln}</div>
        ))}
        <PctContext ctx={histContext} pct={histContext?.skew_spread?.percentile} prefix="Spread at " />
      </div>

      {callSkew > 0.95 && (
        <div style={{ fontSize: 10, color: C.amber, lineHeight: 1.5, marginTop: 4 }}>
          ⚡ Call skew near parity — historically rare, precedes vol expansion or squeeze
        </div>
      )}
      {putSkew < 1.05 && (
        <div style={{ fontSize: 10, color: C.amber, lineHeight: 1.5, marginTop: 4 }}>
          ⚡ Put skew compressed — downside protection historically cheap at these levels
        </div>
      )}

      {inverted != null && (
        <div style={{ fontSize: 9, color: C.textDim, marginTop: 8 }}>
          Term Structure: <span style={{ color: inverted ? C.red : C.green, fontWeight: 700 }}>{inverted ? "INVERTED" : "CONTANGO"}</span>
          {" · "}VIX {fmt(vixCur, 1)} vs 3M {fmt(vix3m, 1)}
        </div>
      )}
    </div>
  );
}

// ── Mini sparkline (pure SVG polyline — lightweight for tiny inline charts) ──
// 80×24, no axes / labels / grid. Returns null (text-only) with < 5 numeric
// points, so a cold start / short history simply shows no line.
function Sparkline({ data, color, width = 80, height = 24 }) {
  const vals = (Array.isArray(data) ? data : []).filter((v) => typeof v === "number" && Number.isFinite(v));
  if (vals.length < 5) return null;
  const min = Math.min(...vals), max = Math.max(...vals), range = (max - min) || 1;
  const pad = 2, w = width - pad * 2, h = height - pad * 2;
  const points = vals
    .map((v, i) => {
      const x = pad + (i / (vals.length - 1)) * w;
      const y = pad + h - ((v - min) / range) * h;   // higher value → higher on chart
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg width={width} height={height} style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <polyline points={points} fill="none" stroke={color} strokeWidth={1.5}
                strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// ── Historical percentile context line (shared by the options panels) ──
// Renders "{prefix}{N}th pct · {days}d", optionally with a sparkline to its left.
// Graceful: nothing when history is absent/insufficient or the percentile is null.
// When `spark` has < 5 points it renders text-only (identical to before).
function _pctColor(pct) {
  return pct > 80 ? C.red : pct < 20 ? C.green : C.textDim;
}
function PctContext({ ctx, pct, prefix = "", spark, sparkColor }) {
  if (!ctx || ctx.insufficient_history || pct == null) return null;
  const hasSpark = Array.isArray(spark) && spark.filter((v) => typeof v === "number").length >= 5;
  if (!hasSpark) {
    return (
      <div style={{ fontSize: 9, color: C.textDim, marginTop: 1 }}>
        {prefix}<span style={{ color: _pctColor(pct) }}>{pct}th pct</span> · {ctx.history_days}d
      </div>
    );
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 1, fontSize: 9, color: C.textDim }}>
      <Sparkline data={spark} color={sparkColor || C.textDim} />
      <span>{prefix}<span style={{ color: _pctColor(pct) }}>{pct}th pct</span> · {ctx.history_days}d</span>
    </div>
  );
}

// ── Historical analogs section (collapsible; renders nothing when data null) ──
function AnalogSection({ data }) {
  const [expanded, setExpanded] = useState(false);
  if (!data) return null;
  const { metricCount, total, matches } = data;
  const shown = expanded ? matches : matches.slice(0, 3);
  return (
    <div style={{ ...s.panel, padding: "10px 12px", marginTop: 10 }}>
      <div style={{ fontSize: 9, color: C.textDim, letterSpacing: "0.1em", fontWeight: 700, marginBottom: 6 }}>
        HISTORICAL ANALOGS
      </div>
      <div style={{ fontSize: 9, color: C.textDim, marginBottom: 6 }}>
        Periods where {metricCount} metric{metricCount === 1 ? "" : "s"} looked similar to today
        {" "}({total} match{total === 1 ? "" : "es"} found)
      </div>
      {total === 0 ? (
        <div style={{ fontSize: 9, color: C.textDim }}>No similar configurations found in available history</div>
      ) : (
        <>
          {shown.map((m) => (
            <div key={m.date} style={{ fontSize: 9, color: C.textDim, marginBottom: 2 }}>
              {m.date} — {m.readings.map((r) => `${r.label}: ${r.pct}th`).join(" · ")}
            </div>
          ))}
          {!expanded && matches.length > 3 && (
            <div onClick={() => setExpanded(true)}
                 style={{ fontSize: 9, color: C.amber, cursor: "pointer", marginTop: 2 }}>
              [+{matches.length - 3} more]
            </div>
          )}
        </>
      )}
      <div style={{ fontSize: 8, color: C.textDim, fontStyle: "italic", marginTop: 6 }}>
        Forward return analysis available after price history accumulates
      </div>
    </div>
  );
}

export function OptionsTab({ keys, onData }) {
  const [ticker,      setTicker]      = useState("SPY");
  const [inputTicker, setInputTicker] = useState("SPY");
  const [expirations, setExpirations] = useState([]);
  const [selectedExp, setSelectedExp] = useState("");
  const [chain,       setChain]       = useState({ calls: [], puts: [] });
  const [quote,       setQuote]       = useState(null);
  const [vix,         setVix]         = useState(null);
  const [skew,        setSkew]        = useState(null);
  const [pcRatio,     setPcRatio]     = useState(null);
  const [gexData,     setGexData]     = useState([]);
  // Backend-computed gate (routers/market_data.py::_gex_validity): false when the
  // chain carries too little live gamma to form a GEX profile — an expired expiry
  // still returns a full chain, but with every greek 0.0, which renders as flat
  // bars on the zero line and a meaningless "dealer magnet". FAIL-OPEN: absent
  // flag = true, so direct-browser mode and any older backend behave as before.
  const [gexValid,    setGexValid]    = useState(true);
  const [magnet,      setMagnet]      = useState({ strike: null, type: null });
  const [unusual,     setUnusual]     = useState([]);
  const [ivRank,      setIvRank]      = useState(null);
  const [showGreeks,  setShowGreeks]  = useState(false);
  const [hasRealGreeks, setHasRealGreeks] = useState(false);
  const [greeksSource, setGreeksSource] = useState(null);   // "tradier" | "alpaca" | "yahoo_approx" | null
  const [loading,     setLoading]     = useState(false);
  const [chainStatus, setChainStatus] = useState("offline");
  const [vixStatus,   setVixStatus]   = useState("offline");
  const [error,       setError]       = useState("");
  const [termStructure,  setTermStructure]  = useState(null);
  const [rBergomiParams, setRBergomiParams] = useState(null);
  const [tsLoading,      setTsLoading]      = useState(false);
  const [multiChain,     setMultiChain]     = useState(null);   // Part A: multi-expiry chain data layer
  const [zeroDte,        setZeroDte]        = useState(null);   // 0DTE nearest-expiry chain + availability
  const [histContext,    setHistContext]    = useState(null);   // /api/options/history/context percentiles
  const [sparkData,      setSparkData]      = useState({});     // {vrp:[...], pc:[...]} 60-day series
  const [analogData,     setAnalogData]     = useState({});     // {vrp:[{date,value}], pc:[...]} 365-day series
  const [earningsData,   setEarningsData]   = useState(null);   // next earnings for the loaded symbol (or null)

  // Bumped on every ticker load. A fetch whose captured id no longer matches is
  // stale and must not apply its results — this prevents a slower prior-ticker
  // fetch from clobbering (or wiping) the current ticker's GEX / unusual table.
  const fetchIdRef = useRef(0);

  // ── rBergomi vol term-structure fetch — parallel to (and independent of) the
  // chain fetch. Keyed on the symbol; never touches chain / GEX / Heston. ──
  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    setTsLoading(true);
    setTermStructure(null);
    setRBergomiParams(null);
    setMultiChain(null);
    (async () => {
      try {
        // Fetch the term-structure endpoint AND the multi-expiry chain in
        // parallel. Multi-expiry is ADDITIVE: it enriches the rBergomi term
        // structure when present, and is the data layer for future Heston
        // cross-sectional calibration. A multi-chain failure never blocks or
        // degrades the existing flow.
        const [tsRes, multiRes] = await Promise.allSettled([
          backendFetch(
            "https://api.tradier.com/v1/markets/options/expirations",  // dead direct fallback
            `/api/options/termstructure/${ticker}`
          ),
          backendFetch(
            `${TRADIER_BASE}/markets/options/chain/multi/${ticker}?count=4`,  // backend-only endpoint
            `/api/options/chain/multi/${ticker}?count=4`
          ),
        ]);
        if (cancelled) return;

        let endpointTs = null;
        if (tsRes.status === "fulfilled") {
          try { endpointTs = await tsRes.value.json(); } catch {}
        }
        let multi = null;
        if (multiRes.status === "fulfilled") {
          try {
            const md = await multiRes.value.json();
            if (md && Array.isArray(md.expiries)) multi = md;
          } catch {}
        }
        if (cancelled) return;
        setMultiChain(multi);

        // rBergomi source: when multi-expiry data is available, merge its
        // ATM-IV points into the endpoint term structure (richer, wider DTE);
        // otherwise use the endpoint term structure exactly as before.
        const derived = deriveTermStructureFromMulti(multi, multi?.spot);
        const ts = derived ? (mergeTermStructures(endpointTs, derived) || endpointTs) : endpointTs;
        setTermStructure(ts);
        setRBergomiParams(calibrateRBergomi(ts));
      } catch {
        if (!cancelled) { setTermStructure(null); setRBergomiParams(null); setMultiChain(null); }
      } finally {
        if (!cancelled) setTsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ticker]);

  // ── Earnings date for the loaded symbol — powers the AI analyst context.
  // Best-effort: any failure or a null response clears earningsData and never
  // blocks the options analysis. Mirrors the other [ticker]-keyed fetches. ──
  useEffect(() => {
    if (!ticker) { setEarningsData(null); return; }
    let cancelled = false;
    setEarningsData(null);
    (async () => {
      try {
        const res = await backendFetch(
          "https://api.nasdaq.com/api/calendar/earnings",   // dead direct fallback (browser-blocked)
          `/api/earnings/ticker/${ticker}`
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setEarningsData(data && data.date ? data : null);
      } catch {
        if (!cancelled) setEarningsData(null);
      }
    })();
    return () => { cancelled = true; };
  }, [ticker]);

  // ── 0DTE chain fetch — automatic, independent of the user's expiry selection.
  // Keyed on `expirations` (which updates atomically with the loaded ticker) so
  // it always targets the current symbol's nearest expiry without a ticker race.
  // Reuses the existing chain endpoint; never a new API. ──
  useEffect(() => {
    if (!expirations.length) { setZeroDte(null); return; }
    const sym = ticker;
    const nearest = String(expirations[0].ts).slice(0, 10);
    const { is0dte, hoursToExpiry } = classify0DTE(nearest);
    if (!is0dte) { setZeroDte({ available: false, symbol: sym, expiry: nearest }); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await backendFetch(
          `${TRADIER_BASE}/markets/options/chains?symbol=${sym}&expiration=${nearest}&greeks=true`,
          `/api/options/chain/${sym}/${nearest}`,
          { headers: { "Accept": "application/json" } }
        );
        const data = await res.json();
        if (cancelled) return;
        const opts = Array.isArray(data?.options) ? data.options : (data?.options?.option || []);
        const mk0 = (o) => ({ strike: o.strike, volume: o.volume || 0, oi: o.open_interest || 0, gamma: o.greeks?.gamma ?? 0 });
        let calls = opts.filter((o) => o.option_type === "call").map(mk0);
        let puts  = opts.filter((o) => o.option_type === "put").map(mk0);
        if (!calls.length && !puts.length && (data?.calls || data?.puts)) {
          const mkY = (o) => ({ strike: o.strike, volume: o.volume || 0, oi: o.openInterest || 0, gamma: o.gamma ?? 0 });
          calls = (data.calls || []).map(mkY);
          puts  = (data.puts  || []).map(mkY);
        }
        if (!calls.length && !puts.length) { setZeroDte({ available: false, symbol: sym, expiry: nearest }); return; }
        setZeroDte({ available: true, symbol: sym, expiry: nearest, calls, puts, hoursToExpiry });
      } catch {
        if (!cancelled) setZeroDte({ available: false, symbol: sym, expiry: nearest });
      }
    })();
    return () => { cancelled = true; };
  }, [expirations]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Historical percentile context (per symbol) — additive, graceful ──
  useEffect(() => {
    if (!ticker) { setHistContext(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await backendFetch(
          `${TRADIER_BASE}/markets/options/history/context/${ticker}`,  // backend-only endpoint
          `/api/options/history/context/${ticker}`
        );
        const data = await res.json();
        if (cancelled) return;
        // null when history is absent/insufficient (<20d) → panels show no context.
        setHistContext(data && data.insufficient_history !== true ? data : null);
      } catch {
        if (!cancelled) setHistContext(null);
      }
    })();
    return () => { cancelled = true; };
  }, [ticker]);

  // ── 60-day sparkline series — fetched in parallel to (and independent of) the
  // histContext fetch. VRP always (deep history); P/C only when it has >= 20d. ──
  useEffect(() => {
    if (!ticker || !histContext) { setSparkData({}); return; }
    let cancelled = false;
    const pcEnough = (histContext.pc_volume?.history_days ?? 0) >= 20;
    const pull = async (path, field) => {
      try {
        const res = await backendFetch(`${TRADIER_BASE}/markets/options/history`, path);  // backend-only
        const arr = await res.json();
        return Array.isArray(arr) ? arr.map((d) => d?.[field]).filter((v) => typeof v === "number") : null;
      } catch {
        return null;
      }
    };
    (async () => {
      const [vrp, pc] = await Promise.all([
        pull(`/api/options/history/vrp/${ticker}?days=60`, "vrp"),
        pcEnough ? pull(`/api/options/history/pc/${ticker}?days=60`, "pc_volume_ratio") : Promise.resolve(null),
      ]);
      if (cancelled) return;
      const next = {};
      if (vrp && vrp.length >= 5) next.vrp = vrp;   // cold start: skip if < 5 points
      if (pc && pc.length >= 5) next.pc = pc;
      setSparkData(next);
    })();
    return () => { cancelled = true; };
  }, [ticker, histContext]);

  // ── 365-day series ({date,value}) for the historical-analog lookup. Parallel
  // to, and independent of, sparkData. VRP always; P/C only when it has >= 20d. ──
  useEffect(() => {
    if (!ticker || !histContext) { setAnalogData({}); return; }
    let cancelled = false;
    const pcEnough = (histContext.pc_volume?.history_days ?? 0) >= 20;
    const pull = async (path, field) => {
      try {
        const res = await backendFetch(`${TRADIER_BASE}/markets/options/history`, path);  // backend-only
        const arr = await res.json();
        return Array.isArray(arr)
          ? arr.filter((d) => d && d.date && typeof d[field] === "number")
               .map((d) => ({ date: String(d.date).slice(0, 10), value: d[field] }))
          : null;
      } catch {
        return null;
      }
    };
    (async () => {
      const [vrp, pc] = await Promise.all([
        pull(`/api/options/history/vrp/${ticker}?days=365`, "vrp"),
        pcEnough ? pull(`/api/options/history/pc/${ticker}?days=365`, "pc_volume_ratio") : Promise.resolve(null),
      ]);
      if (cancelled) return;
      const next = {};
      if (vrp && vrp.length) next.vrp = vrp;
      if (pc && pc.length) next.pc = pc;
      setAnalogData(next);
    })();
    return () => { cancelled = true; };
  }, [ticker, histContext]);

  // ── Historical analogs: past dates whose available-metric percentiles (within
  // the 365d window) are within 15 pts of today's. Null (hidden) unless >= 2
  // metrics have >= 20d history, one is at an extreme, and the analog data is
  // deep enough (>= 30 pts). Forward returns deferred (no price history yet). ──
  const analogSection = useMemo(() => {
    if (!histContext) return null;
    const KEYS = ["gex", "pc_volume", "pc_oi", "vrp", "skew_spread"];
    const suff = KEYS.filter((k) => (histContext[k]?.history_days ?? 0) >= 20).length;
    if (suff < 2) return null;                                  // need >= 2 metrics with history
    const extreme = KEYS.some((k) => {
      const p = histContext[k]?.percentile;
      return p != null && (p > 80 || p < 20);
    });
    if (!extreme) return null;                                  // need >= 1 extreme reading

    const defs = [
      { label: "VRP", series: analogData.vrp },
      { label: "P/C", series: analogData.pc },
    ].filter((d) => Array.isArray(d.series) && d.series.length >= 20);
    const maxLen = Math.max(0, ...defs.map((d) => d.series.length));
    if (!defs.length || maxLen < 30) return null;               // cold start

    const enriched = defs.map((m) => {
      const sorted = m.series.map((r) => r.value).sort((a, b) => a - b);
      const pctOf = (v) => Math.round((sorted.filter((x) => x < v).length / sorted.length) * 100);
      const byDate = {};
      for (const r of m.series) byDate[r.date] = pctOf(r.value);
      const last = m.series[m.series.length - 1];
      return { label: m.label, byDate, todayPct: pctOf(last.value), todayDate: last.date };
    });

    let dates = Object.keys(enriched[0].byDate);
    for (let i = 1; i < enriched.length; i++) dates = dates.filter((d) => d in enriched[i].byDate);
    const todayDates = new Set(enriched.map((e) => e.todayDate));
    dates = dates.filter((d) => !todayDates.has(d));

    const matches = [];
    for (const d of dates) {
      const readings = [];
      let ok = true;
      for (const e of enriched) {
        const p = e.byDate[d];
        if (p == null || Math.abs(p - e.todayPct) > 15) { ok = false; break; }
        readings.push({ label: e.label, pct: p });
      }
      if (ok) matches.push({ date: d, readings });
    }
    matches.sort((a, b) => (a.date < b.date ? 1 : -1));         // most recent first
    return { metricCount: enriched.length, total: matches.length, matches: matches.slice(0, 5) };
  }, [histContext, analogData]);

  // ── VIX + SKEW from FRED ──
  useEffect(() => {
    if (!keys.fredKey && !window.QUANT_SIGNAL_BACKEND) return;
    const go = async () => {
      try {
        const [vr, sr, hvr] = await Promise.all([
          backendFetch(`${FRED_BASE}?series_id=VIXCLS&api_key=${keys.fredKey}&file_type=json&limit=30&sort_order=desc`, `/api/fred/VIXCLS?limit=30`),
          backendFetch(`${FRED_BASE}?series_id=VXVCLS&api_key=${keys.fredKey}&file_type=json&limit=1&sort_order=desc`, `/api/fred/VXVCLS?limit=1`),
          backendFetch(`${FRED_BASE}?series_id=VIXCLS&api_key=${keys.fredKey}&file_type=json&limit=252&sort_order=desc`, `/api/fred/VIXCLS?limit=252`),
        ]);
        const [vd, sd, hvd] = await Promise.all([vr.json(), sr.json(), hvr.json()]);

        if (vd.observations) {
          const obs  = vd.observations.filter((o) => o.value !== ".").slice(0, 20).reverse();
          const cur  = parseFloat(obs[obs.length - 1]?.value);
          const allV = (hvd.observations || []).filter((o) => o.value !== ".").map((o) => parseFloat(o.value));
          const mn   = Math.min(...allV);
          const mx   = Math.max(...allV);
          const rank = mx > mn ? ((cur - mn) / (mx - mn)) * 100 : null;
          const vixObj = {
            current: cur,
            history: obs.map((o) => ({ date: o.date.slice(5), value: parseFloat(o.value) })),
            min52:   mn,
            max52:   mx,
          };
          setVix(vixObj);
          setIvRank(rank);
          setVixStatus("live");
          if (onData) onData("vix", vixObj);
        }
        if (sd.observations?.[0]) {
          const sv = parseFloat(sd.observations[0].value);
          setSkew(sv);
          if (onData) onData("skew", sv);
        }
      } catch {
        setVixStatus("offline");
      }
    };
    go();
  }, [keys.fredKey]);

  // ── Spot price from Alpaca ──
  const fetchQuote = useCallback(async (sym) => {
    if (!(keys.alpacaKey && keys.alpacaSecret) && !window.QUANT_SIGNAL_BACKEND) return null;
    try {
      const res = await backendFetch(
        `${ALPACA_REST}/stocks/snapshots?symbols=${sym}`,
        `/api/snapshot?symbols=${sym}`
      );
      const data = await res.json();
      const snap = data[sym];
      if (snap) {
        const q = {
          price:     snap.latestTrade?.p,
          changePct: ((snap.latestTrade?.p - snap.prevDailyBar?.c) / snap.prevDailyBar?.c) * 100,
          dayHigh:   snap.dailyBar?.h ?? null,
          dayLow:    snap.dailyBar?.l ?? null,
          dayVolume: snap.dailyBar?.v ?? null,
          prevClose: snap.prevDailyBar?.c ?? null,
        };
        setQuote(q);
        return q;   // return the fresh quote so the caller can pass the right spot
      }
    } catch {}
    return null;
  }, [keys]);

  // ── Process chain into derived metrics ──
  const processChain = useCallback((calls, puts, spot, ticker, real, expiration) => {
    setHasRealGreeks(real);

    // Push raw chain to the data bus (ChartsTab reads this for the vol smile).
    // ⚠⚠ `symbol` and `expiration` are load-bearing, not decoration. This payload
    // used to be a bare { calls, puts }, so a consumer had no way to tell WHICH
    // instrument or tenor it held — ChartsTab rendered whatever was last pushed
    // against its own chart's spot, which silently produced another ticker's
    // smile whenever the two tabs were on different symbols. Any reader must
    // check `symbol` before using this.
    if (onData) onData("optionsChain", { calls, puts, symbol: ticker, expiration });

    // Put/call ratios
    const cv = calls.reduce((a, c) => a + (c.volume || 0), 0);
    const pv = puts.reduce((a,  p) => a + (p.volume || 0), 0);
    const co = calls.reduce((a, c) => a + (c.oi || 0), 0);
    const po = puts.reduce((a,  p) => a + (p.oi || 0), 0);
    const pcObj = {
      volumeRatio: cv > 0 ? (pv / cv).toFixed(2) : "—",
      oiRatio:     co > 0 ? (po / co).toFixed(2) : "—",
      callVol: cv, putVol: pv, callOI: co, putOI: po,
    };
    setPcRatio(pcObj);
    if (onData) onData("pcRatio", pcObj);

    // GEX
    const gex = computeGex(calls, puts, spot);
    setGexData(gex);
    setMagnet(findMagnet(gex));

    // Unusual volume
    const unu = computeUnusual(calls, puts, ticker);
    setUnusual(unu);
    if (onData) onData("unusualOptions", unu);
  }, [onData]);

  // ── Fetch chain — Tradier primary, Yahoo fallback ──
  const fetchChain = useCallback(async (sym, expDate, fetchId, spotArg) => {
    if (!expDate) return;
    // Share the caller's id when threaded from fetchExpirations; a direct call
    // (expiration dropdown) claims a fresh id so it supersedes older fetches.
    const myId = fetchId != null ? fetchId : ++fetchIdRef.current;
    setLoading(true);
    setChainStatus("offline");
    // Reset the gate with the other per-fetch status. Safe against the stale-fetch
    // race: this runs synchronously at call time, and a superseded fetch bails at
    // its `myId !== fetchIdRef.current` guard before it can write a stale value.
    setGexValid(true);
    setError("");
    let gotReal = false;
    // Prefer the spot passed in (fresh, for THIS ticker) over the closure quote,
    // which is the previous ticker's price during a change — using it would make
    // the ±20% GEX filter drop every strike and the GEX chart vanish.
    const spot = spotArg || quote?.price || 0;

    // Tradier via backend (real Greeks) — backend falls back to Yahoo internally
    if (keys.tradierKey || window.QUANT_SIGNAL_BACKEND) {
      try {
        const res = await backendFetch(
          `${TRADIER_BASE}/markets/options/chains?symbol=${sym}&expiration=${expDate}&greeks=true`,
          `/api/options/chain/${sym}/${expDate}`,
          { headers: { "Accept": "application/json" } }
        );
        const data = await res.json();
        if (myId !== fetchIdRef.current) return;   // stale ticker fetch — drop it
        const realGreeks = data?.greeks_source === "tradier" || data?.greeks_source === "alpaca";
        // Backend returns a flat `options` array; direct Tradier returns { options: { option: [...] } }.
        const opts = Array.isArray(data?.options)
          ? data.options
          : (data?.options?.option || []);

        if (opts.length) {
          const mk = (o, isCall) => ({
            strike:     o.strike,
            bid:        o.bid,
            ask:        o.ask,
            last:       o.last,
            volume:     o.volume     || 0,
            oi:         o.open_interest || 0,
            iv:         o.greeks?.mid_iv ? (o.greeks.mid_iv * 100).toFixed(1) : "—",
            delta:      o.greeks?.delta?.toFixed(3)  || "—",
            gamma:      o.greeks?.gamma?.toFixed(5)  || "—",
            theta:      o.greeks?.theta?.toFixed(4)  || "—",
            vega:       o.greeks?.vega?.toFixed(4)   || "—",
            itm:        isCall ? o.strike < spot : o.strike > spot,
            greeksReal: realGreeks && !!o.greeks?.delta,
          });
          const calls = opts.filter((o) => o.option_type === "call").map((o) => mk(o, true));
          const puts  = opts.filter((o) => o.option_type === "put").map((o) => mk(o, false));
          setChain({ calls, puts });
          processChain(calls, puts, spot, sym, realGreeks, expDate);
          gotReal = true;
          setGreeksSource(data?.greeks_source || null);
          setChainStatus(realGreeks ? "live" : "delayed");
          // Gate the GEX profile on the backend's validity flag. This is the
          // alpaca/tradier branch — the only one carrying real greeks, so the only
          // one the flag is meaningful for. `!== false` keeps it fail-open.
          setGexValid(data?.gex_valid !== false);
        } else if (data?.calls || data?.puts) {
          // Backend Yahoo-approx shape: { greeks_source:"yahoo_approx", calls:[...], puts:[...] }
          // ⚠ DELIBERATELY EXEMPT FROM THE GEX GATE. The backend stamps this branch
          // gex_valid:false / "no greeks" because Yahoo ships no greeks — but the
          // gamma here is synthesized client-side by approxGamma(), so the chart is
          // still drawable and already self-labels via the "⚠ Approximated Greeks"
          // warning in GexChart. Applying the gate would delete a working fallback
          // display and tell the user to "select a future expiry", which would not
          // help. gexValid stays true (reset at the top of fetchChain).
          const ySpot = spot;
          const mkC = (c) => ({
            strike:     c.strike,
            bid:        c.bid,
            ask:        c.ask,
            last:       c.lastPrice,
            volume:     c.volume      || 0,
            oi:         c.openInterest || 0,
            iv:         c.impliedVolatility ? (c.impliedVolatility * 100).toFixed(1) : "—",
            delta:      c.delta?.toFixed(3) || approxDelta(c.strike, ySpot, true),
            gamma:      c.gamma?.toFixed(5) || approxGamma(c.strike, ySpot),
            theta:      "—",
            vega:       "—",
            itm:        c.strike < ySpot,
            greeksReal: !!c.delta,
          });
          const mkP = (p) => ({
            strike:     p.strike,
            bid:        p.bid,
            ask:        p.ask,
            last:       p.lastPrice,
            volume:     p.volume       || 0,
            oi:         p.openInterest || 0,
            iv:         p.impliedVolatility ? (p.impliedVolatility * 100).toFixed(1) : "—",
            delta:      p.delta?.toFixed(3) || approxDelta(p.strike, ySpot, false),
            gamma:      p.gamma?.toFixed(5) || approxGamma(p.strike, ySpot),
            theta:      "—",
            vega:       "—",
            itm:        p.strike > ySpot,
            greeksReal: !!p.delta,
          });
          const calls = (data.calls || []).map(mkC);
          const puts  = (data.puts  || []).map(mkP);
          setChain({ calls, puts });
          processChain(calls, puts, ySpot, sym, false, expDate);
          gotReal = true;
          setGreeksSource("yahoo_approx");
          setChainStatus("delayed");
        }
      } catch {
        setChainStatus("offline");
      }
    }

    // Yahoo Finance fallback (direct/browser mode) — routed to the same backend chain route.
    // ⚠ Also exempt from the GEX gate, for the same reason as the yahoo-approx branch
    // above: greeks are approximated client-side, and in true direct-browser mode the
    // response carries no gex_valid at all. gexValid stays true (fail-open).
    if (!gotReal) {
      try {
        const expTs = Math.floor(new Date(expDate).getTime() / 1000);
        const res   = await backendFetch(
          `https://query1.finance.yahoo.com/v7/finance/options/${sym}?date=${expTs}`,
          `/api/options/chain/${sym}/${expDate}`
        );
        const data   = await res.json();
        if (myId !== fetchIdRef.current) return;   // stale ticker fetch — drop it
        const result = data?.optionChain?.result?.[0];
        const ySpot  = result?.quote?.regularMarketPrice || spot;

        if (result?.options?.[0]) {
          const mkC = (c) => ({
            strike:     c.strike,
            bid:        c.bid,
            ask:        c.ask,
            last:       c.lastPrice,
            volume:     c.volume      || 0,
            oi:         c.openInterest || 0,
            iv:         c.impliedVolatility ? (c.impliedVolatility * 100).toFixed(1) : "—",
            delta:      c.delta?.toFixed(3) || approxDelta(c.strike, ySpot, true),
            gamma:      c.gamma?.toFixed(5) || approxGamma(c.strike, ySpot),
            theta:      "—",
            vega:       "—",
            itm:        c.strike < ySpot,
            greeksReal: !!c.delta,
          });
          const mkP = (p) => ({
            strike:     p.strike,
            bid:        p.bid,
            ask:        p.ask,
            last:       p.lastPrice,
            volume:     p.volume       || 0,
            oi:         p.openInterest || 0,
            iv:         p.impliedVolatility ? (p.impliedVolatility * 100).toFixed(1) : "—",
            delta:      p.delta?.toFixed(3) || approxDelta(p.strike, ySpot, false),
            gamma:      p.gamma?.toFixed(5) || approxGamma(p.strike, ySpot),
            theta:      "—",
            vega:       "—",
            itm:        p.strike > ySpot,
            greeksReal: !!p.delta,
          });
          const calls = (result.options[0].calls || []).map(mkC);
          const puts  = (result.options[0].puts  || []).map(mkP);
          setChain({ calls, puts });
          processChain(calls, puts, ySpot, sym, false, expDate);
          setGreeksSource("yahoo_approx");
          setChainStatus("delayed");
        } else {
          setError(`No chain data returned for ${sym}. Check ticker.`);
        }
      } catch {
        setError("Data temporarily unavailable — try again in a moment.");
        setChainStatus("offline");
      }
    }
    if (myId === fetchIdRef.current) setLoading(false);
  }, [keys, quote, processChain]);

  // ── Fetch expirations ──
  const fetchExpirations = useCallback(async (sym) => {
    const myId = ++fetchIdRef.current;   // this load supersedes any in-flight one
    setLoading(true);
    setChain({ calls: [], puts: [] });
    setGexData([]);
    setGexValid(true);
    setMagnet({ strike: null, type: null });
    setPcRatio(null);
    setUnusual([]);
    setError("");
    const q = await fetchQuote(sym);
    if (myId !== fetchIdRef.current) return;   // a newer ticker load started — bail
    const freshSpot = q?.price || 0;

    let expList = [];

    if (keys.tradierKey || window.QUANT_SIGNAL_BACKEND) {
      try {
        const res  = await backendFetch(
          `${TRADIER_BASE}/markets/options/expirations?symbol=${sym}`,
          `/api/options/expirations/${sym}`,
          { headers: { "Accept": "application/json" } }
        );
        const data = await res.json();
        if (myId !== fetchIdRef.current) return;
        // Backend returns a flat array of date strings; direct Tradier returns { expirations: { date: [...] } }.
        if (Array.isArray(data?.expirations)) {
          expList = data.expirations.map((d) => ({ ts: d, label: d }));
        } else if (data?.expirations?.date) {
          const raw = Array.isArray(data.expirations.date)
            ? data.expirations.date
            : [data.expirations.date];
          expList = raw.map((d) => ({ ts: d, label: d }));
        }
      } catch {}
    }

    if (!expList.length) {
      try {
        const res    = await backendFetch(
          `https://query1.finance.yahoo.com/v7/finance/options/${sym}`,
          `/api/options/expirations/${sym}`
        );
        const data   = await res.json();
        if (myId !== fetchIdRef.current) return;
        // Backend flat array shape first, then direct Yahoo shape.
        if (Array.isArray(data?.expirations)) {
          expList = data.expirations.map((d) => ({ ts: d, label: d }));
        } else {
          const result = data?.optionChain?.result?.[0];
          if (result?.expirationDates) {
            expList = result.expirationDates.map((ts) => {
              const d = new Date(ts * 1000);
              return { ts: d.toISOString().slice(0, 10), label: d.toISOString().slice(0, 10) };
            });
          }
        }
        if (!expList.length) {
          setError(`Could not load expirations for ${sym}. Verify ticker.`);
        }
      } catch {
        if (myId === fetchIdRef.current) setError(`Could not load expirations for ${sym}. Verify ticker.`);
      }
    }

    if (myId !== fetchIdRef.current) return;
    setExpirations(expList);
    if (expList[0]) {
      setSelectedExp(expList[0].ts);
      await fetchChain(sym, expList[0].ts, myId, freshSpot);
    }
    if (myId === fetchIdRef.current) setLoading(false);
  }, [keys, fetchQuote, fetchChain]);

  // ── Init — restore last ticker ──
  useEffect(() => {
    const saved = localStorage.getItem("fterm_options_ticker") || "SPY";
    setTicker(saved);
    setInputTicker(saved);
    fetchExpirations(saved);
  }, []);

  const spot = quote?.price || 0;

  // Number of multi-expiry chains that carried options (for the rBergomi
  // robustness note). 0 when multi-expiry data is unavailable.
  const multiExpiryCount = (multiChain?.expiries || []).filter((e) => (e?.options || []).length > 0).length;

  // ── 0DTE metrics — computed client-side from the fetched 0DTE chain ──
  const zeroDteMetrics = useMemo(() => {
    if (!zeroDte?.available) return null;
    const { calls, puts, expiry } = zeroDte;
    const gexData = computeGex(calls, puts, spot);
    const netGex = gexData.reduce((a, g) => a + g.gex, 0);
    let gammaFlip = null;   // sign-change strike nearest spot (mirrors GexChart)
    for (let i = 1; i < gexData.length; i++) {
      const a = gexData[i - 1], b = gexData[i];
      if (a.gex !== 0 && b.gex !== 0 && Math.sign(a.gex) !== Math.sign(b.gex)) {
        const cand = Math.abs(b.gex) < Math.abs(a.gex) ? b.strike : a.strike;
        if (gammaFlip === null || (spot > 0 && Math.abs(cand - spot) < Math.abs(gammaFlip - spot))) gammaFlip = cand;
      }
    }
    const sum = (arr, k) => arr.reduce((a, o) => a + (o[k] || 0), 0);
    const callVol = sum(calls, "volume"), putVol = sum(puts, "volume");
    const callOI = sum(calls, "oi"), putOI = sum(puts, "oi");
    const pcVol = callVol > 0 ? putVol / callVol : null;
    const pcOI = callOI > 0 ? putOI / callOI : null;
    const maxPain = computeMaxPain(calls, puts);
    const topCalls = [...calls].sort((a, b) => (b.oi || 0) - (a.oi || 0)).slice(0, 3);
    const topPuts = [...puts].sort((a, b) => (b.oi || 0) - (a.oi || 0)).slice(0, 3);
    const { hoursToExpiry } = classify0DTE(expiry);
    const regime = netGex >= 0 ? "LONG_GAMMA" : "SHORT_GAMMA";
    return { netGex, gammaFlip, pcVol, pcOI, maxPain, topCalls, topPuts, hoursToExpiry, regime };
  }, [zeroDte, spot]);

  // Publish 0DTE intel to the Signal Board bus (globalSignals.zeroDTE).
  useEffect(() => {
    if (onData && zeroDte?.available && zeroDteMetrics) {
      onData("zeroDTE", {
        symbol:        zeroDte.symbol,
        expiry:        zeroDte.expiry,
        netGex:        zeroDteMetrics.netGex,
        pcRatio:       { volume: zeroDteMetrics.pcVol, oi: zeroDteMetrics.pcOI },
        maxPain:       zeroDteMetrics.maxPain,
        gammaFlip:     zeroDteMetrics.gammaFlip,
        hoursToExpiry: zeroDteMetrics.hoursToExpiry,
        regime:        zeroDteMetrics.regime,
      });
    }
  }, [zeroDteMetrics]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 25-delta vol skew — computed client-side from the selected-expiry chain ──
  const skewData = useMemo(() => {
    const pnum = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : null; };
    const toRows = (arr) => (arr || [])
      .map((o) => ({ strike: o.strike, delta: pnum(o.delta), iv: pnum(o.iv) }))
      .filter((o) => o.delta != null && o.iv != null && o.iv > 0 && typeof o.strike === "number");
    const vCalls = toRows(chain.calls);
    const vPuts = toRows(chain.puts);
    if (!vCalls.length || !vPuts.length || !(spot > 0)) return null;
    // ATM IV = call nearest spot (call side preferred, matches the backend ATM point).
    const atmCall = vCalls.reduce((b, o) => (Math.abs(o.strike - spot) < Math.abs(b.strike - spot) ? o : b));
    const atmIVpct = atmCall.iv;
    if (!(atmIVpct > 0)) return null;
    // 25-delta call (delta → 0.25) and put (|delta| → 0.25). iv is a % so ratios are unitless.
    const c25 = vCalls.reduce((b, o) => (Math.abs(o.delta - 0.25) < Math.abs(b.delta - 0.25) ? o : b));
    const p25 = vPuts.reduce((b, o) => (Math.abs(Math.abs(o.delta) - 0.25) < Math.abs(Math.abs(b.delta) - 0.25) ? o : b));
    const callSkew = c25.iv / atmIVpct;
    const putSkew = p25.iv / atmIVpct;
    return {
      callSkew, putSkew, spread: callSkew - putSkew,
      atmIV: atmIVpct / 100, delta25CallIV: c25.iv / 100, delta25PutIV: p25.iv / 100,   // decimals for the bus
    };
  }, [chain, spot]);

  // ── Options-positioning enrichment for the GEX AI analyst ──
  // Dollar-terms figures the model reads alongside the GEX profile. Each degrades
  // to null when its inputs are missing so the context can omit it cleanly.
  const netGexDollars = gexData.length ? gexData.reduce((a, g) => a + g.gex, 0) : null;
  const largestPosGex = gexData.filter((g) => g.gex > 0).sort((a, b) => b.gex - a.gex)[0] || null;
  const largestNegGex = gexData.filter((g) => g.gex < 0).sort((a, b) => a.gex - b.gex)[0] || null;
  const netDealerDelta = (chain.calls.length || chain.puts.length) && spot > 0
    ? computeNetDealerDelta(chain.calls, chain.puts, spot)
    : null;
  const daysToExp = (() => {
    const ymd = String(selectedExp || "").slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
    return Math.max(0, Math.ceil((_expiryCloseMs(ymd) - Date.now()) / 86400000));
  })();

  // Publish 25-delta skew to the Signal Board bus (globalSignals.volSkew).
  useEffect(() => {
    if (onData && skewData) {
      onData("volSkew", {
        symbol:        ticker,
        callSkew:      skewData.callSkew,
        putSkew:       skewData.putSkew,
        spread:        skewData.spread,
        atm_iv:        skewData.atmIV,
        delta25CallIV: skewData.delta25CallIV,
        delta25PutIV:  skewData.delta25PutIV,
      });
    }
  }, [skewData]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTicker = () => {
    const sym = inputTicker.trim().toUpperCase();
    if (!sym) return;
    setTicker(sym);
    localStorage.setItem("fterm_options_ticker", sym);
    fetchExpirations(sym);
  };

  return (
    <div>
      {/* ── Top metrics row ── */}
      {/* ⚠⚠ auto-fit + minmax, NOT four fixed tracks. Each cell here is a whole
          s.panel — VIX gauge, term structure, put/call — not a label and a value,
          so the floor is large: a 30px-bold reading (~72px) plus s.panelB's 20px
          padding and 2px border, against an s.panelH carrying s.panelT and a
          DataStatus side by side. That lands near 110px, so four tracks needed
          ~464px against the 387px this grid gets on a folded phone.
          ⚠ 170 IS A LEGIBILITY FLOOR, NOT THE MIN-CONTENT. 110px is where these
          panels stop fitting; 170 is where they stop being worth reading, and it
          is what forces 2-across on a phone instead of four 90px slivers that
          technically fit. Stating the difference because the two numbers are
          doing different jobs.
          ⚠ Four items over three columns leaves a lone panel on row two between
          roughly 550px and 730px. Unavoidable with equal tracks — there is always
          a 3-column band — and a stranded panel beats four unreadable ones.
          ⚠⚠ UNMEASURED, like every constant in this pass. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 8, marginBottom: 10 }}>

        {/* VIX */}
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>VIX</span>
            <DataStatus status={vixStatus} source="" />
          </div>
          <VixGauge vix={vix} ivRank={ivRank} status={vixStatus} />
          {histContext?.vrp?.current != null && (() => {
            const vrp = histContext.vrp.current;
            const rich = vrp > 0.02, cheap = vrp < -0.02;
            const label = rich ? `VRP: +${fmt(vrp, 3)} (options rich)`
                        : cheap ? `VRP: ${fmt(vrp, 3)} (options cheap)`
                        : "VRP: ~0 (fairly priced)";
            const color = rich ? C.amber : cheap ? C.green : C.textDim;
            return (
              <div style={{ padding: "0 12px 8px" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color }}>{label}</div>
                <PctContext ctx={histContext} pct={histContext?.vrp?.percentile} spark={sparkData.vrp} sparkColor={C.amber} />
              </div>
            );
          })()}
        </div>

        {/* VIX term structure — VIX vs 3-month VIX (VXVCLS) */}
        {(() => {
          const vixCur = typeof vix?.current === "number" ? vix.current : null;
          const vix3m  = typeof skew === "number" ? skew : null;   // `skew` state now holds VXVCLS
          const spread = vixCur != null && vix3m != null ? vixCur - vix3m : null;
          const inverted = spread != null && spread > 0;
          const spreadColor = spread == null ? C.textMid : inverted ? C.red : C.green;
          return (
            <div style={s.panel}>
              <div style={s.panelH}>
                <span style={s.panelT}>VIX 3M</span>
                <DataStatus status={vixStatus} source="" />
              </div>
              <div style={s.panelB}>
                <div style={s.lbl}>3-MONTH IMPLIED VOL (VXVCLS)</div>
                <div style={{
                  fontSize: 30,
                  fontWeight: 700,
                  color: spreadColor,
                  lineHeight: 1,
                }}>
                  {vix3m != null ? fmt(vix3m, 1) : "—"}
                </div>
                <div style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: spreadColor,
                  marginTop: 2,
                }}>
                  {spread == null
                    ? "AWAITING VIX + 3M"
                    : `${inverted ? "INVERTED" : "CONTANGO"} · VIX − 3M = ${spread > 0 ? "+" : ""}${fmt(spread, 1)}`}
                </div>
                <div style={{ fontSize: 11, color: C.textDim, marginTop: 8, lineHeight: 1.6 }}>
                  VIX above 3M (VXVCLS) = term structure inverted — short-term fear elevated. VIX below 3M = normal contango.
                </div>
              </div>
            </div>
          );
        })()}

        {/* Put/Call ratio */}
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>PUT / CALL</span>
            <span style={{ ...s.tag(), fontSize: 11 }}>{ticker}</span>
          </div>
          <div style={s.panelB}>
            {pcRatio ? (
              <>
                <div style={s.lbl}>VOLUME RATIO (PUT ÷ CALL)</div>
                <div style={{
                  fontSize: 30,
                  fontWeight: 700,
                  lineHeight: 1,
                  color: parseFloat(pcRatio.volumeRatio) > 1.2
                    ? C.red
                    : parseFloat(pcRatio.volumeRatio) < 0.7
                    ? C.green
                    : C.amber,
                }}>
                  {pcRatio.volumeRatio}
                </div>
                <PctContext ctx={histContext} pct={histContext?.pc_volume?.percentile} spark={sparkData.pc} sparkColor={C.textDim} />
                <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>
                  OI ratio: {pcRatio.oiRatio}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <PctContext ctx={histContext} pct={histContext?.pc_oi?.percentile} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5, fontSize: 11 }}>
                  <div><div style={s.lbl}>CALL VOL</div><div style={{ color: C.green }}>{fmtK(pcRatio.callVol)}</div></div>
                  <div><div style={s.lbl}>PUT VOL</div><div style={{ color: C.red }}>{fmtK(pcRatio.putVol)}</div></div>
                  <div><div style={s.lbl}>CALL OI</div><div style={{ color: C.green }}>{fmtK(pcRatio.callOI)}</div></div>
                  <div><div style={s.lbl}>PUT OI</div><div style={{ color: C.red }}>{fmtK(pcRatio.putOI)}</div></div>
                </div>
              </>
            ) : (
              <div style={{ color: C.textDim, fontSize: 13 }}>Load chain below</div>
            )}
          </div>
        </div>

        {/* Dealer Magnet */}
        <div style={s.panel}>
          <div style={s.panelH}><span style={s.panelT}>DEALER MAGNET</span></div>
          <div style={s.panelB}>
            {magnet.strike ? (
              <>
                <div style={s.lbl}>
                  {magnet.type === "PIN" ? "⊕ POSITIVE GEX — PINNING" : "⊘ NEGATIVE GEX — ACCELERATION"}
                </div>
                <div style={{
                  fontSize: 30,
                  fontWeight: 700,
                  color: magnet.type === "PIN" ? C.teal : C.red,
                  lineHeight: 1,
                  marginTop: 2,
                  marginBottom: 6,
                }}>
                  ${magnet.strike}
                </div>
                <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.65 }}>
                  {magnet.type === "PIN"
                    ? "Dealers long Gamma at this strike. Hedging buys dips and sells rallies — dampening force. Price gravitates here near expiration."
                    : "Dealers short Gamma here. Hedging amplifies moves — acceleration zone. Breaks through this level can run."}
                </div>
                <div style={{ marginTop: 8, padding: "6px 8px", background: C.tealBg, border: `1px solid ${C.teal}33`, borderRadius: 2 }}>
                  <div style={{ fontSize: 11, color: C.teal, fontWeight: 700, marginBottom: 2 }}>CHARM EFFECT</div>
                  <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.6 }}>
                    As expiration approaches, Charm (dΔ/dt) causes dealer hedges to mechanically unwind, creating drift toward the magnet even without news. Strongest in final 5 days.
                  </div>
                </div>
              </>
            ) : (
              <div style={{ color: C.textDim, fontSize: 13 }}>Load chain to compute</div>
            )}
          </div>
        </div>
      </div>

      {/* ── Chain search bar ── */}
      <div style={s.panel}>
        <div style={s.panelH}>
          <span style={s.panelT}>OPTIONS CHAIN — {ticker}</span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <DataStatus
              status={chainStatus}
              source={keys.tradierKey ? "" : ""}
            />
            {!keys.tradierKey && (
              <span style={{ ...s.tag(C.amber), fontSize: 11 }}>
                Black-Scholes Greeks
              </span>
            )}
          </div>
        </div>
        <div style={s.panelB}>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <input
              style={{ ...s.inp, width: 90 }}
              value={inputTicker}
              placeholder="TICKER"
              onChange={(e) => setInputTicker(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && loadTicker()}
            />
            <button style={s.btn("amber")} onClick={loadTicker}>
              LOAD
            </button>
            {expirations.length > 0 && (
              <select
                style={{ ...s.inp, width: "auto", cursor: "pointer" }}
                value={selectedExp}
                onChange={(e) => {
                  setSelectedExp(e.target.value);
                  fetchChain(ticker, e.target.value);
                }}
              >
                {expirations.map((e) => (
                  <option key={e.ts} value={e.ts}>{e.label}</option>
                ))}
              </select>
            )}
            <button
              style={s.btn(showGreeks ? "teal" : "default")}
              onClick={() => setShowGreeks((p) => !p)}
            >
              {showGreeks ? "HIDE GREEKS Δ Γ Θ V" : "SHOW GREEKS Δ Γ Θ V"}
            </button>
            {quote && (
              <div style={{ marginLeft: "auto" }}>
                <span style={{ fontSize: 16, fontWeight: 700 }}>${fmt(quote.price)}</span>
                <span style={{
                  fontSize: 14,
                  color: (quote.changePct || 0) >= 0 ? C.green : C.red,
                  marginLeft: 8,
                }}>
                  {pct(quote.changePct)}
                </span>
              </div>
            )}
          </div>
          {error && (
            <div style={{ color: C.red, fontSize: 13, marginTop: 8 }}>
              <AlertCircle size={10} style={{ marginRight: 4, verticalAlign: "middle" }} />
              {error}
            </div>
          )}
          {loading && (
            <div style={{ color: C.textDim, fontSize: 13, padding: 14, textAlign: "center" }}>
              LOADING OPTIONS DATA...
            </div>
          )}
        </div>
      </div>

      {/* ── GEX Profile ── */}
      {gexData.length > 0 && (
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>GAMMA EXPOSURE PROFILE (GEX)</span>
            <span style={{ ...s.tag(), fontSize: 11 }}>
              Positive = dealer long Gamma (stabilizing) · Negative = dealer short Gamma (accelerating)
            </span>
          </div>
          <div style={s.panelB}>
            {!gexValid ? (
              // The chain loaded but carries no usable gamma, so every strike would
              // compute GEX 0 — flat bars and a meaningless magnet. Show why instead
              // of a chart that looks like a real (empty) reading, and withhold
              // SYNTHESIZE so the analysis endpoint is never called on dead greeks.
              <div style={{ color: C.textDim, fontSize: 13, padding: 14, textAlign: "center", lineHeight: 1.6 }}>
                GEX data unavailable — options data for this expiry has expired.
                Select a future expiry.
              </div>
            ) : (
            <>
            <GexChart
              gexData={gexData}
              spot={spot}
              magnet={magnet}
              hasRealGreeks={hasRealGreeks}
              greeksSource={greeksSource}
              histContext={histContext}
            />
            {(keys.anthropicKey || window.QUANT_SIGNAL_BACKEND) && (
              <div style={{ marginTop: 10 }}>
                <AnalystButton
                  panel="gex"
                  label="SYNTHESIZE"
                  context={
                    `GEX analysis for ${ticker} — spot $${fmt(spot)}, exp ${selectedExp}` +
                    (daysToExp != null ? ` (${daysToExp}d to expiry)` : "") + `.\n` +
                    `Dealer magnet: $${magnet.strike} (${magnet.type}).\n` +
                    `Put/call volume ratio: ${pcRatio?.volumeRatio}, OI ratio: ${pcRatio?.oiRatio}.\n` +
                    `VIX: ${vix?.current ? fmt(vix.current) : "N/A"}, VIX3M: ${skew ? fmt(skew, 1) : "N/A"}` +
                    (ivRank != null ? `, IV rank (52w): ${fmt(ivRank, 0)}th pct` : "") + `.\n` +
                    `Greeks: ${greeksLabel(hasRealGreeks, greeksSource).toLowerCase()}.\n` +
                    `GEX profile has ${gexData.length} strikes.\n` +
                    (netGexDollars != null
                      ? `Net GEX: $${fmt(netGexDollars / 1e6, 1)}M ` +
                        `(${netGexDollars >= 0 ? "net long gamma / pinning" : "net short gamma / accelerant"}).\n`
                      : "") +
                    (largestPosGex
                      ? `Largest positive GEX strike: $${largestPosGex.strike} (+$${fmt(largestPosGex.gex / 1e6, 2)}M). `
                      : "") +
                    (largestNegGex
                      ? `Largest negative GEX strike: $${largestNegGex.strike} (-$${fmt(Math.abs(largestNegGex.gex) / 1e6, 2)}M).\n`
                      : "\n") +
                    (netDealerDelta != null
                      ? `Net dealer delta (OI-weighted, dealers assumed short customer OI): ` +
                        `$${fmt(netDealerDelta / 1e6, 1)}M — ` +
                        `${netDealerDelta >= 0 ? "net long (sell into strength)" : "net short (buy into strength)"}.\n`
                      : "") +
                    earningsContextLine(earningsData) +
                    `\nAnalyze: dealer positioning implications, what the magnet level means mechanically, ` +
                    `how the current VIX vs 3-month (VIX3M) term structure interacts with this GEX structure, ` +
                    `and whether the put/call ratio is consistent with or contradicts the GEX picture.` +
                    (rBergomiParams
                      ? `\n\nROUGH VOLATILITY (rBergomi):\n` +
                        `Hurst H=${fmt(rBergomiParams.H, 3)} (${rbergomiHLabel(rBergomiParams.H).text})\n` +
                        `Calibrated from ${rBergomiParams.termStructurePoints}-point term structure\n` +
                        `R²=${fmt(rBergomiParams.r2, 2)} fit quality: ${rBergomiParams.fit}\n` +
                        `Vol/Vol η=${fmt(rBergomiParams.eta, 3)} (approx) | Correl ρ=${fmt(rBergomiParams.rho, 3)} (approx)\n` +
                        `Short-dated: ${rBergomiParams.shortDatedFit}\n` +
                        `Recommendation: ${rBergomiParams.recommendation}`
                      : "")
                  }
                />
              </div>
            )}
            </>
            )}
          </div>
        </div>
      )}

      {/* ── Unusual volume ── */}
      <UnusualTable
        unusual={unusual}
        ticker={ticker}
        anthropicKey={keys.anthropicKey}
        quote={quote}
        ivRank={ivRank}
        earningsData={earningsData}
      />

      {/* ── Chain tables ── */}
      {!loading && (chain.calls.length > 0 || chain.puts.length > 0) && (
        /* ⚠⚠ THE ONLY GRID HERE WHOSE CELLS ARE THEMSELVES SCROLLING TABLES.
            Each ChainTable is an s.panel holding up to 11 columns (STRIKE LAST
            BID ASK VOL OI IV% + Δ Γ Θ V). Two fixed tracks put each of them in
            ~190px on a folded phone — the table scrolls internally now, but a
            190px viewport onto an 11-column chain is not a readable surface.
            ⚠ 340 is derived from the ChainTable's OWN floor, not picked. Its
            header row alone is ~319px (per-column text at fontSize 11 plus 12px
            padding each: STRIKE 52, LAST 38, BID 32, ASK 32, VOL 32, OI 25,
            IV% 32, four greeks ~19 each), and fontSize 13 body values push it
            past 350. So 340 sits just under the point where a table can nearly
            show itself unaided: side-by-side only happens once each side has
            close to enough room, which is ~768px. Below that they stack.
            ⚠⚠ UNMEASURED — advance-width arithmetic, not a render. */
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 8 }}>
          <ChainTable
            data={chain.calls}
            label="CALLS"
            color={C.green}
            spot={spot}
            showGreeks={showGreeks}
            storageKey="qs_calls_expanded"
          />
          <ChainTable
            data={chain.puts}
            label="PUTS"
            color={C.red}
            spot={spot}
            showGreeks={showGreeks}
            storageKey="qs_puts_expanded"
          />
        </div>
      )}

      {/* ── Greeks reference ── */}
      {showGreeks && chain.calls.length > 0 && (
        <GreeksReference hasRealGreeks={hasRealGreeks} />
      )}

      {/* ── 0DTE intelligence (independent nearest-expiry fetch) ── */}
      <ZeroDtePanel data={zeroDte} metrics={zeroDteMetrics} spot={spot} />

      {/* ── Vol skew (25-delta, from the selected-expiry chain) ── */}
      <SkewPanel data={skewData} vixCur={vix?.current ?? null} vix3m={typeof skew === "number" ? skew : null} histContext={histContext} />

      {/* ── Historical analogs (client-side lookup over 365d percentile history) ── */}
      <AnalogSection data={analogSection} />

      {/* ── Heston vol surface ── */}
      {chain.calls.length > 0 && (
        <HestonSurfacePanel
          chain={[...chain.calls, ...chain.puts]}
          spot={spot}
          multiChain={multiChain}
          expiration={selectedExp}
          anthropicKey={keys.anthropicKey}
          backendUrl={keys.backendUrl}
          onResult={(result) => {
            if (!onData) return;
            // ⚠ Shape: { surface, symbol, expiration }, not a bare array — a
            // consumer could not otherwise tell which instrument or tenor it held,
            // and ChartsTab was overlaying this surface's model IVs onto a smile
            // that may be a different expiry entirely.
            // ⚠⚠ `expiration` comes STRAIGHT FROM hestonFitChain, which now reports
            // the expiry its display surface was actually priced at. It replaced a
            // local re-derivation of that rule — which could not see the fitter's
            // own ±30% moneyness band or its <4-point bail, so it could name an
            // expiry the fitter had already discarded. Do not reintroduce a copy
            // here; if the tenor is needed elsewhere, return it from the fitter.
            // Absent (e.g. the dead /api/heston backend path) → null → ChartsTab
            // omits the overlay rather than guessing.
            onData("hestonSurface", {
              surface:    result.surface,
              symbol:     ticker,
              expiration: result.expiration || null,
            });
          }}
        />
      )}

      {/* ── rBergomi rough volatility (independent term-structure fetch) ── */}
      <RBergomiPanel termStructure={termStructure} result={rBergomiParams} loading={tsLoading} multiExpiryCount={multiExpiryCount} />

      <div style={{ fontSize: 11, color: C.textDim, marginTop: 6, lineHeight: 1.6 }}>
        Greeks marked * are approximated.{" "}
        <span style={{ color: "#FFD700", fontWeight: 700 }}>Not a recommendation — consult a licensed financial advisor for decisions.</span>
      </div>
    </div>
  );
}
